import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { fetchChannels } from '../../store/slices/channelSlice';
import { isTaskCommentChannel } from '../../utils/channelTypeUtils';
import notificationService from '../../services/notificationService';

import { API_URL } from '../../config/api';
import { appendNeedToCallItem } from '../../features/notes/services/noteService';
import { withUnderscoresForUpload } from '../../utils/uploadFilename';
import { uploadFilesWithProgress, inferAttachmentFileType } from '../../utils/uploadFilesWithProgress';
import { extractUrlFromText, fixMessageLinkHtml, isLinkAttachment, linkDomainFromUrl, composeFileName, splitFileNameParts, readBlobDurationSeconds } from '../../utils/fileUtils';
import ConfirmModal from '../common/ConfirmModal';
import DriveFolderNameModal from '../google/DriveFolderNameModal';
import {
  connectGooglePlugin,
  getGooglePluginStatus,
  uploadAttachmentsToGoogleDrive,
} from '../../services/googlePluginService';
import { queryRealm, realmAvailable } from '../../realm';
import {
  deleteLocalFile,
  filesToLocalAttachments,
  isInlineFileSrc,
  isPendingLocalAttachment,
  shouldUseOfflineAttachment,
} from '../../services/offlineAttachmentStore';
import AttachmentGroupBar, { canGroupComposeAttachments } from './AttachmentGroupBar';
import {
  VoiceNoteBanner,
  VoiceNoteMicButton,
  VoiceNoteSendModal,
  blobToVoiceFile,
  captionToHtml,
  useVoiceRecorder,
} from './VoiceNoteRecorder';
import AttachmentComposeModal from './AttachmentComposeModal';
import AttachmentsListModal from './AttachmentsListModal';
import ImageViewerModal from './ImageViewerModal';
import PresignedAvatar from '../common/PresignedAvatar';
import FillMessageTemplateModal from '../templates/FillMessageTemplateModal';
import { fetchTemplatesForChannel } from '../../services/messageTemplateService';
import { deleteVaultItem, fetchVaultItems } from '../../features/vault/services/vaultService';
import { buildVaultZipContents, buildVaultZipFile, vaultItemId } from '../../features/vault/utils/buildVaultZip';
import {
  ALL_MENTION_USERNAME,
  extractMentionUsernames,
  findMentionTriggerIndex,
  findUserByMention,
  isEveryoneMention,
} from '../../utils/mentionDisplayUtils';
import { suggestedRepliesForText, escapeReplyHtml, DEFAULT_QUICK_REPLIES, loadCustomQuickReplies, saveCustomQuickReplies, CALL_ME_BACK_PLEASE, CALL_THIS_PERSON, WILL_CALL_IN_SOMETIME, isCallMeBackPlease, isWillCallInSometime, notifyNeedToCallChanged } from '../../utils/suggestedReplies';
import { stampFileName, textToPdfFile, textToTxtFile } from '../../utils/longTextExportFiles';
import CallThisPersonModal from '../../features/notes/components/CallThisPersonModal';
import { SensitiveSendModal } from './SensitiveMessageModals';

const getDraftKey = (channelId, threadId) => threadId ? `chat_draft_${channelId}_thread_${threadId}` : `chat_draft_${channelId}`;

const TEMPLATE_TYPE_META = {
  ANNOUNCEMENT: {
    label: 'Announcement',
    chip: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800/60',
    iconBg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    activeChip: 'bg-amber-500 text-white border-amber-500 dark:bg-amber-500 dark:text-white dark:border-amber-500',
  },
  WORKFLOW: {
    label: 'Workflow',
    chip: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:border-violet-800/60',
    iconBg: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
    activeChip: 'bg-violet-500 text-white border-violet-500 dark:bg-violet-500 dark:text-white dark:border-violet-500',
  },
  TASK: {
    label: 'Task',
    chip: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-800/60',
    iconBg: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    activeChip: 'bg-sky-500 text-white border-sky-500 dark:bg-sky-500 dark:text-white dark:border-sky-500',
  },
  GENERAL: {
    label: 'General',
    chip: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600',
    iconBg: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    activeChip: 'bg-gray-700 text-white border-gray-700 dark:bg-gray-500 dark:text-white dark:border-gray-500',
  },
  MESSAGE: {
    label: 'Message',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800/60',
    iconBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    activeChip: 'bg-emerald-500 text-white border-emerald-500 dark:bg-emerald-500 dark:text-white dark:border-emerald-500',
  },
  APPOINTMENT: {
    label: 'Appointment',
    chip: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800/60',
    iconBg: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
    activeChip: 'bg-rose-500 text-white border-rose-500 dark:bg-rose-500 dark:text-white dark:border-rose-500',
  },
};

const TEMPLATE_TYPE_FILTER_ORDER = ['MESSAGE', 'APPOINTMENT', 'TASK', 'ANNOUNCEMENT', 'WORKFLOW', 'GENERAL'];

const resolveTemplateTypeMeta = (templateType) => {
  const key = String(templateType || 'GENERAL').toUpperCase();
  return TEMPLATE_TYPE_META[key] || TEMPLATE_TYPE_META.GENERAL;
};

const TemplateTypeIcon = ({ templateType, className = 'w-4 h-4' }) => {
  const type = String(templateType || '').toUpperCase();
  if (type === 'ANNOUNCEMENT') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    );
  }
  if (type === 'WORKFLOW') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }
  if (type === 'TASK') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    );
  }
  if (type === 'MESSAGE') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
      </svg>
    );
  }
  if (type === 'APPOINTMENT') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
    </svg>
  );
};

const QUILL_FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link',
];

const EMOJI_PICKER_WIDTH = 320;
const EMOJI_PICKER_HEIGHT = 400;
const EMOJI_PICKER_GAP = 8;

const computeEmojiPickerPosition = (anchorEl) => {
  if (!anchorEl) {
    return { top: EMOJI_PICKER_GAP, left: EMOJI_PICKER_GAP };
  }

  const rect = anchorEl.getBoundingClientRect();
  let top = rect.top - EMOJI_PICKER_HEIGHT - EMOJI_PICKER_GAP;
  if (top < EMOJI_PICKER_GAP) {
    top = rect.bottom + EMOJI_PICKER_GAP;
  }

  let left = rect.left + rect.width / 2 - EMOJI_PICKER_WIDTH / 2;
  const maxLeft = window.innerWidth - EMOJI_PICKER_WIDTH - EMOJI_PICKER_GAP;
  left = Math.max(EMOJI_PICKER_GAP, Math.min(left, maxLeft));

  const maxTop = window.innerHeight - EMOJI_PICKER_HEIGHT - EMOJI_PICKER_GAP;
  top = Math.max(EMOJI_PICKER_GAP, Math.min(top, maxTop));

  return { top, left };
};

const loadDraftFromStorage = (channelId, threadId) => {
  if (!channelId) return '';
  try {
    const draftData = localStorage.getItem(getDraftKey(channelId, threadId));
    if (draftData) {
      const parsed = JSON.parse(draftData);
      return parsed.content || '';
    }
  } catch (error) {
    console.error('Error loading draft:', error);
  }
  return '';
};

const channelMentionToken = (channel) => (channel?.name || '').trim().replace(/\s+/g, '-');

const isDirectChannel = (channel) => {
  const type = String(channel?.type || '').toUpperCase();
  return type === 'DIRECT' || type === 'DIRECT_MESSAGE' || type === 'DM';
};

const EVERYONE_MENTION = {
  id: '__mention_all__',
  username: ALL_MENTION_USERNAME,
  firstName: 'all',
  lastName: '',
  isEveryoneMention: true,
};

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const mapAttachmentsForSend = (list = []) =>
  list.map((att) => ({
    id: att.id,
    fileName: att.fileName,
    fileUrl: att.fileUrl,
    fileType: att.fileType,
    fileSize: att.fileSize,
    ...(att.durationSeconds != null && Number.isFinite(Number(att.durationSeconds))
      ? { durationSeconds: Number(att.durationSeconds) }
      : {}),
    ...(Array.isArray(att.vaultContents) && att.vaultContents.length
      ? { vaultContents: att.vaultContents }
      : {}),
    ...(isPendingLocalAttachment(att)
      ? { pendingUpload: true, localFileId: att.localFileId || att.id }
      : {}),
  }));

const composerToolBtnClass = (active) =>
  `inline-flex h-7 min-w-[28px] items-center justify-center rounded-md px-1 text-[13px] leading-none transition-colors ${
    active
      ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white'
      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600'
  }`;

const ComposerFormatToolbar = ({ quillRef, activeFormats, onToggleEmoji, emojiActive, sensitiveTagged, onToggleSensitive }) => {
  const apply = (format, value = true) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const current = editor.getFormat() || {};
    if (format === 'list') {
      editor.format('list', current.list === value ? false : value);
    } else {
      editor.format(format, current[format] ? false : value);
    }
    editor.focus();
  };

  const applyLink = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const current = editor.getFormat() || {};
    if (current.link) {
      editor.format('link', false);
    } else {
      const url = window.prompt('Enter link URL');
      if (url) editor.format('link', url);
    }
    editor.focus();
  };

  const clearFormat = () => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (range && range.length > 0) {
      editor.removeFormat(range.index, range.length);
    } else {
      ['bold', 'italic', 'underline', 'strike', 'list', 'link'].forEach((fmt) => {
        editor.format(fmt, false);
      });
    }
    editor.focus();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/80">
      <button type="button" className={`${composerToolBtnClass(Boolean(activeFormats.bold))} font-bold`} title="Bold" aria-label="Bold" onClick={() => apply('bold')}>B</button>
      <button type="button" className={`${composerToolBtnClass(Boolean(activeFormats.italic))} italic`} title="Italic" aria-label="Italic" onClick={() => apply('italic')}>I</button>
      <button type="button" className={`${composerToolBtnClass(Boolean(activeFormats.underline))} underline`} title="Underline" aria-label="Underline" onClick={() => apply('underline')}>U</button>
      <button type="button" className={`${composerToolBtnClass(Boolean(activeFormats.strike))} line-through`} title="Strikethrough" aria-label="Strikethrough" onClick={() => apply('strike')}>S</button>
      <span className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-600" aria-hidden="true" />
      <button type="button" className={composerToolBtnClass(emojiActive)} title="Insert emoji" aria-label="Insert emoji" onClick={(event) => onToggleEmoji(event.currentTarget)}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
      <button type="button" className={composerToolBtnClass(activeFormats.list === 'ordered')} title="Numbered list" aria-label="Numbered list" onClick={() => apply('list', 'ordered')}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
      </button>
      <button type="button" className={composerToolBtnClass(activeFormats.list === 'bullet')} title="Bullet list" aria-label="Bullet list" onClick={() => apply('list', 'bullet')}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M4 6a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
      </button>
      <button type="button" className={composerToolBtnClass(Boolean(activeFormats.link))} title="Link" aria-label="Link" onClick={applyLink}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      </button>
      <button type="button" className={composerToolBtnClass(false)} title="Clear formatting" aria-label="Clear formatting" onClick={clearFormat}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <span className="mx-1 h-4 w-px bg-gray-200 dark:bg-gray-600" aria-hidden="true" />
      <button
        type="button"
        className={composerToolBtnClass(sensitiveTagged)}
        title="Sensitive message (PIN)"
        aria-label="Sensitive message"
        aria-pressed={sensitiveTagged}
        onClick={onToggleSensitive}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      </button>
    </div>
  );
};

const pastedAttachmentSummary = (files) => {
  const fileCount = files.length;
  const imageCount = files.filter((f) => f.fileType === 'IMAGE').length;
  const otherCount = fileCount - imageCount;
  if (imageCount > 0 && otherCount > 0) {
    return `${imageCount} image(s) and ${otherCount} file(s) have been attached.`;
  }
  if (imageCount > 0) {
    return `${imageCount} image(s) have been attached to your message.`;
  }
  return `${fileCount} file(s) have been attached to your message.`;
};

const MessageInput = ({ onSendMessage, onTyping, channelId, replyToMessage, onCancelReply, isThreadInput = false, autoFocusComposer = true, onTemplatePostingChange, onCreateEvent }) => {
  const notifyTyping = (active) => {
    if (typeof onTyping === 'function') {
      onTyping(active);
    }
  };
  const dispatch = useDispatch();
  const handleVoicePermissionError = useCallback((message) => {
    dispatch(addNotification({
      type: 'error',
      title: 'Voice note',
      message,
    }));
  }, [dispatch]);
  const voice = useVoiceRecorder({
    onPermissionError: handleVoicePermissionError,
  });
  const voiceApiRef = useRef(voice);
  voiceApiRef.current = voice;
  const { users = [] } = useSelector((state) => state.users);
  const { channels = [] } = useSelector((state) => state.channels);
  const { user: currentUser } = useSelector((state) => state.auth);
  const latestIncomingMessage = useSelector((state) => {
    if (isThreadInput && replyToMessage && replyToMessage.senderId !== currentUser?.id) {
      return replyToMessage;
    }
    const list = state.chat?.messages?.[channelId] || [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const item = list[i];
      if (!item || item.deleted) continue;
      if (currentUser?.id && item.senderId === currentUser.id) continue;
      return item;
    }
    return null;
  });
  const liveChannel = useSelector((state) => {
    const list = [
      ...(state.channels?.channels || []),
      ...(state.channels?.directMessages || []),
    ];
    return list.find((channel) => channel?.id === channelId) || state.chat?.currentChannel || null;
  });
  const channelMemberIds = Array.isArray(liveChannel?.memberIds) ? liveChannel.memberIds.filter(Boolean) : [];
  const callThisPersonInitialUser = useMemo(() => {
    if (!isDirectChannel(liveChannel) || !currentUser?.id) return null;
    const otherId = channelMemberIds.find((id) => id && String(id) !== String(currentUser.id));
    if (!otherId) return null;
    return users.find((user) => String(user.id) === String(otherId)) || null;
  }, [liveChannel, currentUser?.id, channelMemberIds, users]);

  const addDmPeerToMyNeedToCall = async (reason) => {
    if (!isDirectChannel(liveChannel) || !currentUser?.id) return false;
    const peer = callThisPersonInitialUser;
    const otherId = channelMemberIds.find((id) => id && String(id) !== String(currentUser.id));
    const targetUserId = peer?.id || otherId;
    if (!targetUserId) return false;
    const name = [peer?.firstName, peer?.lastName].filter(Boolean).join(' ').trim()
      || peer?.username
      || String(liveChannel?.name || '').trim()
      || 'Someone';
    const phone = String(peer?.mobileNumber || peer?.contactNumber || '').trim();
    const items = await appendNeedToCallItem({
      targetUserId,
      name,
      phone,
      reason,
    });
    notifyNeedToCallChanged(items);
    return true;
  };

  const { darkMode } = useSelector((state) => state.ui);
  // replyToMessage is the parent message when isThreadInput is true
  const threadId = isThreadInput && replyToMessage ? replyToMessage.id : null;
  const composerInstanceId = `workloop-composer-${isThreadInput ? 'thread' : 'main'}-${channelId || 'none'}-${threadId || 'root'}`;
  const uploadInputId = isThreadInput ? `file-upload-thread-${channelId}` : `file-upload-main-${channelId}`;
  const [message, setMessage] = useState(() => loadDraftFromStorage(channelId, threadId));
  const [isTyping, setIsTyping] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentGroupName, setAttachmentGroupName] = useState('');
  const [attachmentGroupMode, setAttachmentGroupMode] = useState('none');
  const [attachmentExistingGroupId, setAttachmentExistingGroupId] = useState('');
  const [existingResourceGroups, setExistingResourceGroups] = useState([]);
  const [loadingResourceGroups, setLoadingResourceGroups] = useState(false);
  const [attachmentGroupExpanded, setAttachmentGroupExpanded] = useState(true);
  const [attachmentComposeOpen, setAttachmentComposeOpen] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [confirmConnectDrive, setConfirmConnectDrive] = useState(false);
  const [driveFolderModalOpen, setDriveFolderModalOpen] = useState(false);
  const [driveFolderName, setDriveFolderName] = useState('');
  const [driveUploadDone, setDriveUploadDone] = useState(0);
  const [driveUploadTotal, setDriveUploadTotal] = useState(0);
  const [viewingAllAttachments, setViewingAllAttachments] = useState(false);
  const [viewingGallery, setViewingGallery] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [textExportPrompt, setTextExportPrompt] = useState(null);
  const [sensitiveTagged, setSensitiveTagged] = useState(false);
  const [sensitiveSendOpen, setSensitiveSendOpen] = useState(false);
  const sensitiveTaggedRef = useRef(false);
  const senderPinForSendRef = useRef('');
  const allowReceiverPinForSendRef = useRef(true);
  const sensitiveTitleForSendRef = useRef('');
  const pendingSensitiveActionRef = useRef(null);
  sensitiveTaggedRef.current = sensitiveTagged;
  const [exportFileName, setExportFileName] = useState('');
  const [dismissedSuggestionId, setDismissedSuggestionId] = useState(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [callThisPersonOpen, setCallThisPersonOpen] = useState(false);
  const [customQuickReplies, setCustomQuickReplies] = useState([]);
  const [quickReplyDraft, setQuickReplyDraft] = useState('');
  const quickReplyBoxRef = useRef(null);
  const suggestedReplies = useMemo(() => {
    if (!latestIncomingMessage?.id || dismissedSuggestionId === latestIncomingMessage.id) return [];
    const draft = String(message || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
    if (draft && draft !== '<p><br></p>' && draft !== '<br>') return [];
    if (voice.recording || attachments.length) return [];
    return suggestedRepliesForText(latestIncomingMessage.content || '');
  }, [attachments.length, dismissedSuggestionId, latestIncomingMessage, message, voice.recording]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionType, setMentionType] = useState(null);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeFormats, setActiveFormats] = useState({});
  const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
  const [channelTemplates, setChannelTemplates] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [templateTypeFilter, setTemplateTypeFilter] = useState('ALL');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateHighlightIndex, setTemplateHighlightIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [showVaultMenu, setShowVaultMenu] = useState(false);
  const [selectedVaultIds, setSelectedVaultIds] = useState([]);
  const [vaultSearchQuery, setVaultSearchQuery] = useState('');
  const [vaultBusy, setVaultBusy] = useState(false);
  const quillRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiPickerAnchorRef = useRef(null);
  const bottomEmojiButtonRef = useRef(null);
  const templateMenuRef = useRef(null);
  const templateListRef = useRef(null);
  const templateSearchInputRef = useRef(null);
  const vaultMenuRef = useRef(null);
  const toggleEmojiPickerRef = useRef(() => {});
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const interimTranscriptRef = useRef('');
  const finalTranscriptRef = useRef('');
  const draftSaveTimeoutRef = useRef(null);
  const prevAttachmentCountRef = useRef(0);
  const focusHandlersRef = useRef({ focus: null, blur: null });
  const previewFetchTimeoutRef = useRef(null);
  const previewFetchGenRef = useRef(0);
  const mentionDropdownRef = useRef(null);
  const mentionedUserIdsRef = useRef(new Set());
  const latestDraftContentRef = useRef(loadDraftFromStorage(channelId, threadId));

  const collectMentionedUserIds = useCallback((plainText) => {
    const ids = new Set(mentionedUserIdsRef.current);
    const tokens = extractMentionUsernames(plainText);
    tokens.forEach((token) => {
      if (isEveryoneMention(token)) {
        channelMemberIds.forEach((id) => {
          if (id && id !== currentUser?.id) ids.add(id);
        });
        return;
      }
      const user = findUserByMention(users, token);
      if (user?.id && user.id !== currentUser?.id) {
        ids.add(user.id);
      }
    });
    return Array.from(ids);
  }, [users, currentUser?.id, channelMemberIds]);

  const resetAttachmentGroupState = useCallback(() => {
    setAttachmentGroupName('');
    setAttachmentGroupMode('none');
    setAttachmentExistingGroupId('');
    setAttachmentGroupExpanded(true);
  }, []);

  const resolveGroupSendOptions = useCallback((messageAttachments) => {
    if (!canGroupComposeAttachments(messageAttachments)) {
      return { attachmentGroupName: undefined, targetResourceGroupId: undefined };
    }
    if (attachmentGroupMode === 'new') {
      const name = attachmentGroupName.trim();
      return {
        attachmentGroupName: name || undefined,
        targetResourceGroupId: undefined,
      };
    }
    if (attachmentGroupMode === 'existing' && attachmentExistingGroupId) {
      return {
        attachmentGroupName: undefined,
        targetResourceGroupId: attachmentExistingGroupId,
      };
    }
    return { attachmentGroupName: undefined, targetResourceGroupId: undefined };
  }, [attachmentGroupMode, attachmentGroupName, attachmentExistingGroupId]);

  useEffect(() => {
    if (!channelId || attachments.length < 2) {
      setExistingResourceGroups([]);
      return undefined;
    }

    let cancelled = false;
    const loadGroups = async () => {
      setLoadingResourceGroups(true);
      try {
        if (await realmAvailable()) {
          const rows = await queryRealm({
            type: 'ResourceGroup',
            filter: 'channelId == $0',
            args: [String(channelId)],
          });
          if (!cancelled) {
            setExistingResourceGroups(Array.isArray(rows) ? rows : []);
          }
          return;
        }
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/resource-groups/channel/${channelId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setExistingResourceGroups(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Failed to load resource groups:', error);
        if (!cancelled) {
          setExistingResourceGroups([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingResourceGroups(false);
        }
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [channelId, attachments.length]);

  useEffect(() => {
    const count = attachments.length;
    if (count >= 2 && prevAttachmentCountRef.current < 2) {
      setAttachmentComposeOpen(true);
    }
    if (count < 2) {
      setAttachmentComposeOpen(false);
      resetAttachmentGroupState();
    }
    prevAttachmentCountRef.current = count;
  }, [attachments.length, resetAttachmentGroupState]);

  useEffect(() => {
    let cancelled = false;
    if (!channelId || isThreadInput) {
      setChannelTemplates([]);
      setTemplateTypeFilter('ALL');
      setTemplateSearchQuery('');
      setTemplateHighlightIndex(0);
      return undefined;
    }
    fetchTemplatesForChannel(channelId)
      .then((list) => {
        if (!cancelled) {
          setChannelTemplates(
            (Array.isArray(list) ? list : []).filter(
              (tpl) => String(tpl?.templateType || '').toUpperCase() !== 'SEND_USING_API'
            )
          );
          setTemplateTypeFilter('ALL');
          setTemplateSearchQuery('');
          setTemplateHighlightIndex(0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChannelTemplates([]);
          setTemplateTypeFilter('ALL');
          setTemplateSearchQuery('');
          setTemplateHighlightIndex(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [channelId, isThreadInput]);

  const loadVaultItems = useCallback(() => {
    if (isThreadInput) {
      setVaultItems([]);
      return;
    }
    fetchVaultItems()
      .then((list) => setVaultItems(Array.isArray(list) ? list : []))
      .catch(() => setVaultItems([]));
  }, [isThreadInput]);

  useEffect(() => {
    loadVaultItems();
  }, [loadVaultItems, channelId]);

  useEffect(() => {
    const onVaultUpdated = () => loadVaultItems();
    window.addEventListener('workloop:vault-updated', onVaultUpdated);
    return () => window.removeEventListener('workloop:vault-updated', onVaultUpdated);
  }, [loadVaultItems]);

  // Strip HTML tags from content for preview / draft checks
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  };

  const hasMeaningfulContent = (htmlContent, textContent) => {
    if (textContent && textContent.trim().length > 0) {
      return true;
    }
    if (!htmlContent || htmlContent === '<p><br></p>' || htmlContent === '<p></p>') {
      return false;
    }
    return stripHtmlTags(htmlContent).length > 0;
  };

  const LONG_TEXT_CHARS = 500;
  const composerPlainText = useMemo(() => {
    const fromQuill = quillRef.current?.getEditor?.()?.getText?.();
    if (typeof fromQuill === 'string' && fromQuill.replace(/\s+/g, '')) {
      return fromQuill.replace(/\n+$/, '');
    }
    return stripHtmlTags(message);
  }, [message]);
  const isLongComposerText = composerPlainText.replace(/\s+/g, ' ').trim().length > LONG_TEXT_CHARS;

  const anotherFieldHasFocus = (editorRoot) => {
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) {
      return false;
    }
    if (editorRoot && (active === editorRoot || editorRoot.contains(active))) {
      return false;
    }
    return Boolean(
      active.isContentEditable
      || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)
    );
  };

  // Auto-focus the thread input on mount — never steal from another field.
  useEffect(() => {
    if (!isThreadInput || !autoFocusComposer) return undefined;
    const timeoutId = setTimeout(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor || anotherFieldHasFocus(editor.root)) return;
      editor.focus();
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [isThreadInput, autoFocusComposer, channelId, threadId]);

  const saveDraft = (targetChannelId, content) => {
    if (!targetChannelId) return;
    try {
      const draftData = {
        content: content || '',
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(getDraftKey(targetChannelId, threadId), JSON.stringify(draftData));
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  const clearDraft = (targetChannelId) => {
    if (!targetChannelId) return;
    try {
      localStorage.removeItem(getDraftKey(targetChannelId, threadId));
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  const persistDraft = (targetChannelId, htmlContent, textContent) => {
    if (!targetChannelId) return;
    const text =
      textContent ??
      (() => {
        const quillEditor = quillRef.current?.getEditor();
        return quillEditor?.getText() || stripHtmlTags(htmlContent);
      })();
    if (hasMeaningfulContent(htmlContent, text)) {
      saveDraft(targetChannelId, htmlContent);
    } else {
      clearDraft(targetChannelId);
    }
  };

  const flushDraftForChannel = (targetChannelId) => {
    if (!targetChannelId) return;
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
      draftSaveTimeoutRef.current = null;
    }
    const quillEditor = quillRef.current?.getEditor();
    const htmlContent = latestDraftContentRef.current || quillEditor?.root.innerHTML || '';
    const textContent = quillEditor?.getText() || '';
    persistDraft(targetChannelId, htmlContent, textContent);
  };

  // Track message input focus state for notifications
  useEffect(() => {
    let timeoutId = null;
    let editorElement = null;

    const setupFocusTracking = () => {
      const quillEditor = quillRef.current?.getEditor();
      if (!quillEditor) {
        // Retry if editor not ready yet
        timeoutId = setTimeout(setupFocusTracking, 100);
        return;
      }

      // Create handlers and store in ref for cleanup
      focusHandlersRef.current.focus = () => {
        notificationService.setMessageInputFocused(true);
      };

      focusHandlersRef.current.blur = () => {
        notificationService.setMessageInputFocused(false);
      };

      // Quill editor uses a contenteditable div, so we listen on the root element
      editorElement = quillEditor.root;
      editorElement.addEventListener('focus', focusHandlersRef.current.focus, true);
      editorElement.addEventListener('blur', focusHandlersRef.current.blur, true);

      // Also check initial focus state
      if (document.activeElement === editorElement || editorElement.contains(document.activeElement)) {
        notificationService.setMessageInputFocused(true);
      }
    };

    // Start setup after a short delay to ensure Quill is ready
    timeoutId = setTimeout(setupFocusTracking, 200);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (editorElement && focusHandlersRef.current.focus && focusHandlersRef.current.blur) {
        // Remove event listeners
        editorElement.removeEventListener('focus', focusHandlersRef.current.focus, true);
        editorElement.removeEventListener('blur', focusHandlersRef.current.blur, true);
      }
      // Reset focus state when component unmounts or channel changes
      notificationService.setMessageInputFocused(false);
    };
  }, [channelId]); // Re-setup when channel changes

  const mentionableChannels = useCallback(
    () => channels.filter((channel) => !isDirectChannel(channel) && !isTaskCommentChannel(channel)),
    [channels],
  );

  // Detect @user and #channel mentions and show suggestions
  const detectMentions = useCallback((editor) => {
    if (!editor) return;

        setTimeout(() => {
      const selection = editor.getSelection();
      if (!selection) {
        setShowMentionSuggestions(false);
        setMentionType(null);
        return;
      }

      const text = editor.getText();
      const cursorIndex = selection.index;
      const trigger = findMentionTriggerIndex(text, cursorIndex);
      const triggerIndex = trigger.index;
      const activeType = trigger.type;

      if (triggerIndex === -1 || !activeType) {
        setShowMentionSuggestions(false);
        setMentionType(null);
        return;
      }

      const query = text.substring(triggerIndex + 1, cursorIndex).toLowerCase();

      if (activeType === 'user') {
        if (users.length === 0 && channelMemberIds.length < 2) {
          setShowMentionSuggestions(false);
          setMentionType(null);
          return;
        }

        const filteredUsers = users
          .filter((u) => u.id !== currentUser?.id)
          .filter((u) => {
            if (!query) return true;
            const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
            const username = (u.username || '').toLowerCase();
            return fullName.includes(query) || username.includes(query);
          })
          .slice(0, 10);

        const everyoneMatches = !query
          || ALL_MENTION_USERNAME.startsWith(query)
          || 'everyone'.startsWith(query);
        const suggestions = [];
        if (everyoneMatches && channelMemberIds.length > 1) {
          suggestions.push(EVERYONE_MENTION);
        }
        suggestions.push(...filteredUsers);

        if (suggestions.length > 0) {
          setMentionSuggestions(suggestions);
          setMentionType('user');
          setMentionSearchQuery(query);
          setMentionStartIndex(triggerIndex);
          setSelectedMentionIndex(0);
          setShowMentionSuggestions(true);
        } else {
          setShowMentionSuggestions(false);
          setMentionType(null);
        }
        return;
      }

      const availableChannels = mentionableChannels();
      if (availableChannels.length === 0) {
        setShowMentionSuggestions(false);
        setMentionType(null);
        return;
      }

      const filteredChannels = availableChannels
        .filter((channel) => {
          if (!query) return true;
          const name = (channel.name || '').toLowerCase();
          const slug = channelMentionToken(channel).toLowerCase();
          return name.includes(query) || slug.includes(query);
        })
        .slice(0, 10);

      if (filteredChannels.length > 0) {
        setMentionSuggestions(filteredChannels);
        setMentionType('channel');
        setMentionSearchQuery(query);
        setMentionStartIndex(triggerIndex);
        setSelectedMentionIndex(0);
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
        setMentionType(null);
      }
    }, 10);
  }, [users, currentUser, mentionableChannels, channelMemberIds.length]);

  // Fetch users and channels when component mounts
  useEffect(() => {
    if (users.length === 0) {
      dispatch(fetchUsers());
    }
    if (channels.length === 0) {
      dispatch(fetchChannels());
    }
  }, [dispatch, users.length, channels.length]);

  // Flush draft when leaving this channel (MessageInput remounts per channel via key on parent).
  useLayoutEffect(() => {
    if (!channelId) return;

    const currentChannelId = channelId;
    setAttachments([]);
    mentionedUserIdsRef.current.clear();

    return () => {
      if (currentChannelId) {
        flushDraftForChannel(currentChannelId);
      }
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
        draftSaveTimeoutRef.current = null;
      }
      if (previewFetchTimeoutRef.current) {
        clearTimeout(previewFetchTimeoutRef.current);
        previewFetchTimeoutRef.current = null;
      }
      previewFetchGenRef.current += 1;
      setLinkPreview(null);
      setIsFetchingPreview(false);
      setShowMentionSuggestions(false);
      setMentionType(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on channel switch; draft helpers are stable in behavior
  }, [channelId]);

  // Close mention suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(event.target) &&
        quillRef.current &&
        !quillRef.current.getEditor().root.contains(event.target)
      ) {
        setShowMentionSuggestions(false);
      }
    };

    if (showMentionSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMentionSuggestions]);

  useEffect(() => {
    setCustomQuickReplies(loadCustomQuickReplies(currentUser?.id));
  }, [currentUser?.id]);

  useEffect(() => {
    if (!showQuickReplies) return undefined;
    const handleClickOutside = (event) => {
      if (quickReplyBoxRef.current && !quickReplyBoxRef.current.contains(event.target)) {
        setShowQuickReplies(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickReplies]);


  // Focus input when a reply is initiated. Depend on the message id so a new
  // object reference on parent re-render does not steal focus from other fields.
  const replyToMessageId = replyToMessage?.id || null;
  useEffect(() => {
    if (!autoFocusComposer || !replyToMessageId) return undefined;
    const timeoutId = setTimeout(() => {
      const quillEditor = quillRef.current?.getEditor();
      if (!quillEditor?.root || anotherFieldHasFocus(quillEditor.root)) return;
      quillEditor.root.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      quillEditor.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [replyToMessageId, autoFocusComposer]);

  // Focus the composer when a channel opens, but never pull the caret out of
  // another field (including the other composer) the user is already typing in.
  useEffect(() => {
    if (isThreadInput) return;
    const editor = quillRef.current?.getEditor();
    if (!editor || anotherFieldHasFocus(editor.root)) return;
    editor.focus();
  }, [channelId, isThreadInput]);

  useEffect(() => {
    const quillEditor = quillRef.current?.getEditor();
    if (quillEditor) {
      const syncFormats = () => {
        // getFormat() with no range calls getSelection(true), which focuses this
        // editor. That steals the caret from the other composer and from every
        // workflow/search textbox whenever document selection changes.
        if (!quillEditor.hasFocus()) return;
        const range = quillEditor.getSelection();
        if (!range) {
          setActiveFormats({});
          return;
        }
        setActiveFormats(quillEditor.getFormat(range.index, range.length) || {});
      };
      const handleKeyPress = (e) => {
        if (
          e.key === '@'
          || (e.key === '2' && e.shiftKey)
          || e.key === '#'
          || (e.key === '3' && e.shiftKey)
        ) {
          setTimeout(() => {
            detectMentions(quillEditor);
          }, 50);
        }
      };

      quillEditor.root.addEventListener('keydown', handleKeyPress);
      quillEditor.on('selection-change', syncFormats);
      quillEditor.on('text-change', syncFormats);

    return () => {
        quillEditor.root.removeEventListener('keydown', handleKeyPress);
        quillEditor.off('selection-change', syncFormats);
        quillEditor.off('text-change', syncFormats);
      };
    }
  }, [detectMentions, channelId]);

  const closeEmojiPicker = useCallback(() => {
    emojiPickerAnchorRef.current = null;
    setShowEmojiPicker(false);
  }, []);

  const openEmojiPicker = useCallback((anchorEl) => {
    if (!anchorEl) return;
    emojiPickerAnchorRef.current = anchorEl;
    setEmojiPickerPosition(computeEmojiPickerPosition(anchorEl));
    setShowEmojiPicker(true);
  }, []);

  const toggleEmojiPicker = useCallback((anchorEl) => {
    if (!anchorEl) return;
    if (showEmojiPicker && emojiPickerAnchorRef.current === anchorEl) {
      closeEmojiPicker();
      return;
    }
    openEmojiPicker(anchorEl);
  }, [showEmojiPicker, closeEmojiPicker, openEmojiPicker]);

  toggleEmojiPickerRef.current = (anchorEl) => {
    const anchor = anchorEl || bottomEmojiButtonRef.current;
    toggleEmojiPicker(anchor);
  };

  useLayoutEffect(() => {
    if (!showEmojiPicker || !emojiPickerAnchorRef.current) return undefined;

    const updatePosition = () => {
      const anchor = emojiPickerAnchorRef.current;
      if (anchor) {
        setEmojiPickerPosition(computeEmojiPickerPosition(anchor));
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const handleClickOutside = (event) => {
      const pickerEl = emojiPickerRef.current;
      const anchor = emojiPickerAnchorRef.current;
      if (
        pickerEl?.contains(event.target) ||
        anchor?.contains(event.target)
      ) {
        return;
      }
      closeEmojiPicker();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker, closeEmojiPicker]);

  useEffect(() => {
    if (!showTemplateMenu) return undefined;

    const handleClickOutside = (event) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(event.target)) {
        setShowTemplateMenu(false);
        setTemplateTypeFilter('ALL');
        setTemplateSearchQuery('');
        setTemplateHighlightIndex(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTemplateMenu]);

  useEffect(() => {
    if (!showVaultMenu) return undefined;
    const handleClickOutside = (event) => {
      if (vaultMenuRef.current && !vaultMenuRef.current.contains(event.target)) {
        setShowVaultMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVaultMenu]);

  const insertEmoji = useCallback((emoji) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || !emoji) return;

    let selection = editor.getSelection();
    if (!selection) {
      const length = editor.getLength();
      const index = Math.max(0, length - 1);
      selection = { index, length: 0 };
      editor.setSelection(index, 0);
    }

    editor.insertText(selection.index, emoji);
    editor.setSelection(selection.index + emoji.length);
    editor.root.focus();
    closeEmojiPicker();
  }, [closeEmojiPicker]);

  // Rich Text Editor Configuration
  const quillModules = useMemo(() => ({
    toolbar: false,
    history: { userOnly: true },
  }), []);

  // Detect URL in Quill HTML or plain text (prefers full path over truncated href)
  const detectUrlInEditor = (editor) => {
    const htmlContent = editor?.root?.innerHTML || '';
    const textContent = editor?.getText() || '';
    return extractUrlFromText(htmlContent) || extractUrlFromText(textContent);
  };

  const clearComposerLinkPreview = () => {
    previewFetchGenRef.current += 1;
    if (previewFetchTimeoutRef.current) {
      clearTimeout(previewFetchTimeoutRef.current);
      previewFetchTimeoutRef.current = null;
    }
    setLinkPreview(null);
    setIsFetchingPreview(false);
  };

  // Fetch link preview - try client-side first for authenticated sites, fallback to backend
  const fetchLinkPreview = async (url) => {
    if (!url) {
      clearComposerLinkPreview();
      return;
    }

    previewFetchGenRef.current += 1;
    const fetchGen = previewFetchGenRef.current;
    setIsFetchingPreview(true);
    try {
      // For authenticated sites (like CUMIN), try fetching client-side first
      // The browser has the user's authentication cookies
      try {
        const clientPreview = await fetchLinkPreviewClientSide(url);
        if (fetchGen !== previewFetchGenRef.current) return;
        if (clientPreview && (clientPreview.title || clientPreview.description)) {
          setLinkPreview(clientPreview);
          setIsFetchingPreview(false);
          return;
        }
      } catch (clientError) {
        console.log('Client-side fetch failed (CORS or other issue), trying backend:', clientError.message);
      }

      // Fallback to backend API (for public sites or with auth)
      const token = localStorage.getItem('token');

      // Note: For authenticated sites like CUMIN, we can't access their cookies
      // due to browser security (cross-domain cookie restrictions)
      // The backend will try to fetch, but may fail for authenticated sites
      // In that case, the preview will be fetched asynchronously after message is sent

      const response = await axios.get(`${API_URL}/messages/link-preview`, {
        params: { url },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fetchGen !== previewFetchGenRef.current) return;
      // Use backend result even if it's minimal (URL/domain only)
      // For authenticated sites, this might only have URL/domain
      // The full preview will be attempted asynchronously after message is sent
      setLinkPreview(response.data);
    } catch (error) {
      console.error('Error fetching link preview:', error);
      if (fetchGen !== previewFetchGenRef.current) return;
      setLinkPreview(null);
    } finally {
      if (fetchGen === previewFetchGenRef.current) {
        setIsFetchingPreview(false);
      }
    }
  };

  // Fetch link preview client-side (uses browser's cookies/auth for authenticated sites)
  const fetchLinkPreviewClientSide = async (url) => {
    return new Promise((resolve, reject) => {
      // Try fetching with credentials (cookies) for authenticated sites
      fetch(url, {
        method: 'GET',
        mode: 'cors', // Try CORS first
        credentials: 'include', // Include cookies for authenticated requests
          headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        }
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(html => {
          // Parse HTML to extract OG tags
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          const getMetaContent = (property, name) => {
            const meta = doc.querySelector(`meta[property="${property}"]`) ||
              doc.querySelector(`meta[name="${name}"]`);
            return meta ? meta.getAttribute('content') : null;
          };

          // Decode HTML entities
          const decodeHtml = (text) => {
            if (!text) return '';
            const txt = document.createElement('textarea');
            txt.innerHTML = text;
            return txt.value;
          };

          const ogTitle = getMetaContent('og:title', 'og:title');
          const ogDescription = getMetaContent('og:description', 'og:description');
          const ogImage = getMetaContent('og:image', 'og:image');
          const ogSiteName = getMetaContent('og:site_name', 'og:site_name');

          const title = ogTitle ? decodeHtml(ogTitle) : (doc.querySelector('title')?.textContent || '');
          const description = ogDescription ? decodeHtml(ogDescription) :
            (getMetaContent('description', 'description') ? decodeHtml(getMetaContent('description', 'description')) : '');
          const imageUrl = ogImage ? decodeHtml(ogImage) : null;
          const siteName = ogSiteName ? decodeHtml(ogSiteName) : '';

          // Extract domain from URL
          const urlObj = new URL(url);
          const domain = urlObj.host;

          if (title || description) {
            resolve({
              url,
              title: title.trim(),
              description: description.trim(),
              imageUrl: imageUrl || null,
              siteName: siteName.trim() || null,
              domain: domain
            });
          } else {
            reject(new Error('No preview data found in HTML'));
          }
        })
        .catch(error => {
          // If CORS fails, try no-cors mode (but we can't read response)
          // Or reject and fallback to backend
          reject(error);
        });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSending) return;
    if (voiceApiRef.current?.recording) {
      voiceApiRef.current.stopRecording();
      return;
    }
    
    // Get plain text from Quill editor
    const quillEditor = quillRef.current?.getEditor();
    const textContent = quillEditor?.getText() || '';
    const htmlContent = quillEditor?.root.innerHTML || '';
    
    // Check if message has meaningful content (not just whitespace)
    const hasContent = hasMeaningfulContent(htmlContent, textContent);
    
    if (hasContent || attachments.length > 0) {
      if (canGroupComposeAttachments(attachments) && attachmentGroupMode === 'existing' && !attachmentExistingGroupId) {
        dispatch(addNotification({
          type: 'error',
          title: 'Select a group',
          message: 'Choose an existing Shared Resources group, or switch to "No group" or "New group".',
        }));
        return;
      }

      const proceed = async () => {
      try {
        // Prepare attachments for the message
        const messageAttachments = mapAttachmentsForSend(attachments);

        const { attachmentGroupName: groupNameToSend, targetResourceGroupId } =
          resolveGroupSendOptions(messageAttachments);

        // Send HTML content if it has formatting, otherwise send plain text
        const finalUrl = extractUrlFromText(htmlContent) || extractUrlFromText(textContent);
        let previewToSend = linkPreview;
        if (finalUrl) {
          previewToSend = previewToSend
            ? { ...previewToSend, url: finalUrl }
            : {
                url: finalUrl,
                title: finalUrl,
                domain: linkDomainFromUrl(finalUrl),
              };
        }

        const normalizedHtml = htmlContent !== '<p><br></p>' && htmlContent.trim()
          ? fixMessageLinkHtml(htmlContent, finalUrl)
          : htmlContent;
        const contentToSend = normalizedHtml !== '<p><br></p>' && normalizedHtml.trim()
          ? normalizedHtml
          : textContent.trim();

        setIsSending(true);
        setShowVaultMenu(false);
        setSelectedVaultIds([]);
        setVaultSearchQuery('');

        // Clear input immediately (optimistic update - message already appears in chat)
        if (quillEditor) {
          quillEditor.setText('');
        }
        setMessage('');
        setAttachmentComposeOpen(false);
        setAttachments([]);
        resetAttachmentGroupState();
        clearComposerLinkPreview();
        finalTranscriptRef.current = '';
        interimTranscriptRef.current = '';
        
        // Clear draft after sending message
        if (channelId) {
          if (draftSaveTimeoutRef.current) {
            clearTimeout(draftSaveTimeoutRef.current);
            draftSaveTimeoutRef.current = null;
          }
          clearDraft(channelId);
          latestDraftContentRef.current = '';
        }
        
        // Clear reply state after sending
        if (onCancelReply) {
          onCancelReply();
        }
        
        // Stop typing indicator when message is sent
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        if (isTyping) {
          setIsTyping(false);
          notifyTyping(false);
        }

        const mentionedUserIds = collectMentionedUserIds(textContent);
        mentionedUserIdsRef.current.clear();

        // Send message (optimistic message already appears in chat)
        await onSendMessage(
          contentToSend,
          messageAttachments,
          replyToMessage?.id,
          previewToSend,
          mentionedUserIds,
          groupNameToSend,
          targetResourceGroupId,
          {
            sensitive: sensitiveTaggedRef.current,
            senderPin: senderPinForSendRef.current || undefined,
            allowReceiverPin: allowReceiverPinForSendRef.current,
            sensitiveTitle: sensitiveTitleForSendRef.current || undefined,
          }
        );
        setSensitiveTagged(false);
        senderPinForSendRef.current = '';
        allowReceiverPinForSendRef.current = true;
        sensitiveTitleForSendRef.current = '';
        if (isCallMeBackPlease(textContent) && isDirectChannel(liveChannel)) {
          dispatch(addNotification({
            type: 'success',
            message: 'Asked them to call you back',
          }));
        } else if (isWillCallInSometime(textContent) && isDirectChannel(liveChannel)) {
          try {
            await addDmPeerToMyNeedToCall(WILL_CALL_IN_SOMETIME);
            dispatch(addNotification({
              type: 'success',
              message: 'Added them to your Need to call',
            }));
          } catch (err) {
            notifyNeedToCallChanged();
            dispatch(addNotification({
              type: 'error',
              message: err?.response?.data?.error || err?.message || 'Could not add to Need to call',
            }));
          }
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // Don't restore the message on error - it's already shown in chat with failed status
        // User can retry using the retry button
        // The error notification is already shown by handleSendMessage
      } finally {
        setIsSending(false);
      }
      };
      if (sensitiveTaggedRef.current) {
        pendingSensitiveActionRef.current = proceed;
        setSensitiveSendOpen(true);
        return;
      }
      await proceed();
    }
  };

  const sendSuggestedReply = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || isSending) return;
    setDismissedSuggestionId(latestIncomingMessage?.id || 'sent');
    setShowQuickReplies(false);
    setIsSending(true);
    try {
      await onSendMessage(
        `<p>${escapeReplyHtml(trimmed)}</p>`,
        [],
        isThreadInput ? replyToMessage?.id : undefined,
        null,
        [],
        undefined,
        undefined,
      );
      if (isCallMeBackPlease(trimmed) && isDirectChannel(liveChannel)) {
        dispatch(addNotification({
          type: 'success',
          message: 'Asked them to call you back',
        }));
      } else if (isWillCallInSometime(trimmed) && isDirectChannel(liveChannel)) {
        try {
          await addDmPeerToMyNeedToCall(WILL_CALL_IN_SOMETIME);
          dispatch(addNotification({
            type: 'success',
            message: 'Added them to your Need to call',
          }));
        } catch (err) {
          notifyNeedToCallChanged();
          dispatch(addNotification({
            type: 'error',
            message: err?.response?.data?.error || err?.message || 'Could not add to Need to call',
          }));
        }
      }
    } catch (error) {
      console.error('Error sending suggested reply:', error);
    } finally {
      setIsSending(false);
    }
  };

  const insertMentionText = (mentionText, onInserted) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || mentionStartIndex === -1) return;

    const selection = editor.getSelection();
    if (!selection) return;

    const cursorIndex = selection.index;
    const queryLength = cursorIndex - mentionStartIndex;
    editor.deleteText(mentionStartIndex, queryLength);
    editor.insertText(mentionStartIndex, mentionText);
    editor.setSelection(mentionStartIndex + mentionText.length);

    setShowMentionSuggestions(false);
    setMentionType(null);
    setMentionStartIndex(-1);
    setMentionSearchQuery('');

    onInserted?.();
    editor.root.focus();
  };

  const insertUserMention = (user) => {
    insertMentionText(`@${user.username} `, () => {
      if (user?.isEveryoneMention) {
        channelMemberIds.forEach((id) => {
          if (id && id !== currentUser?.id) {
            mentionedUserIdsRef.current.add(id);
          }
        });
        return;
      }
      if (user?.id) {
        mentionedUserIdsRef.current.add(user.id);
      }
    });
  };

  const insertChannelMention = (channel) => {
    const token = channelMentionToken(channel);
    if (!token) return;
    insertMentionText(`#${token} `);
  };

  const insertSelectedMention = () => {
    const selected = mentionSuggestions[selectedMentionIndex];
    if (!selected) return;
    if (mentionType === 'channel') {
      insertChannelMention(selected);
      return;
    }
    insertUserMention(selected);
  };

  // Insert mention into editor (legacy name kept for callers)
  const insertMention = (user) => {
    insertUserMention(user);
  };

  const handleQuillChange = (content, delta, source, editor) => {
    setMessage(content);
    latestDraftContentRef.current = content;
    
    // Clear existing timeouts immediately on any change
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    if (previewFetchTimeoutRef.current) {
      clearTimeout(previewFetchTimeoutRef.current);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Don't trigger new draft saving, mention detection, or typing indicator for programmatic changes (source === 'api')
    if (source === 'api' || source === 'silent') {
      // If content is empty (programmatic clear), ensure typing indicator is off
      const textContent = editor?.getText() || '';
      if (!textContent || !textContent.trim()) {
        clearComposerLinkPreview();
        if (isTyping) {
          setIsTyping(false);
          notifyTyping(false);
        }
      }
      return;
    }

    // Detect mentions on any manual text change
    detectMentions(editor);

    // Capture channelId at time of change for the debounced save
    const activeChannelId = channelId;

    // Save draft with debounce (save after 1 second of no typing)
    draftSaveTimeoutRef.current = setTimeout(() => {
      if (activeChannelId) {
        const textContent = editor?.getText() || '';
        persistDraft(activeChannelId, content, textContent);
      }
    }, 1000); // Save draft 1 second after user stops typing
    
    // Reset transcript refs when user manually types
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    
    // Detect URL and fetch preview with debounce
    const textContent = editor?.getText() || '';
    const detectedUrl = detectUrlInEditor(editor);

    if (previewFetchTimeoutRef.current) {
      clearTimeout(previewFetchTimeoutRef.current);
      previewFetchTimeoutRef.current = null;
    }
    if (!detectedUrl) {
      clearComposerLinkPreview();
    } else {
      previewFetchTimeoutRef.current = setTimeout(() => {
        fetchLinkPreview(detectedUrl);
      }, 1000);
    }

    if (textContent && textContent.trim()) {
      // User is typing - notify if not already typing
      if (!isTyping) {
        setIsTyping(true);
        notifyTyping(true);
      }
      
      // Reset timeout - stop typing indicator after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        notifyTyping(false);
      }, 2000);
    } else {
      // Input is empty - stop typing immediately
      if (isTyping) {
        setIsTyping(false);
        notifyTyping(false);
      }
    }
  };

  // Handle keyboard navigation for mentions and Enter/Return key to send message
  useEffect(() => {
    const quillEditor = quillRef.current?.getEditor();
    if (!quillEditor) return;

    const handleKeyDown = async (e) => {
      // Handle mention suggestions navigation
      if (showMentionSuggestions && mentionSuggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedMentionIndex((prev) =>
            prev < mentionSuggestions.length - 1 ? prev + 1 : prev
          );
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0));
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          e.stopPropagation();
          insertSelectedMention();
          return;
        }
        if (e.key === 'Escape' || e.keyCode === 27) {
          e.preventDefault();
          setShowMentionSuggestions(false);
          setMentionType(null);
          return;
        }
      }

      // Check if Enter or Return key is pressed (Mac has both keys)
      // keyCode 13 covers both Enter and Return keys
      const isEnterOrReturn = e.key === 'Enter' || e.keyCode === 13;
      
      if (isEnterOrReturn) {
        // If ALT+ENTER/RETURN, SHIFT+ENTER/RETURN, CTRL+ENTER/RETURN, or CMD+ENTER/RETURN, 
        // allow default behavior (new line)
        if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) {
          return; // Allow default behavior - creates new line
        }
        
        // If Enter/Return alone (without modifiers), prevent default and send message
        e.preventDefault();
        e.stopPropagation();
        if (voiceApiRef.current?.recording) {
          voiceApiRef.current.stopRecording();
          return;
        }
        
        // Get current editor content
        const currentTextContent = quillEditor.getText() || '';
        const currentHtmlContent = quillEditor.root.innerHTML || '';
        const hasContent = hasMeaningfulContent(currentHtmlContent, currentTextContent);
        
        // Get current attachments from state
        const currentAttachments = attachments;
        
        // Only submit if there's content or attachments
        if (hasContent || currentAttachments.length > 0) {
          if (isSending) return;
          if (canGroupComposeAttachments(currentAttachments) && attachmentGroupMode === 'existing' && !attachmentExistingGroupId) {
            dispatch(addNotification({
              type: 'error',
              title: 'Select a group',
              message: 'Choose an existing Shared Resources group, or switch to "No group" or "New group".',
            }));
            return;
          }

          const proceed = async () => {
          try {
            // Prepare attachments for the message
            const messageAttachments = mapAttachmentsForSend(currentAttachments);

            const { attachmentGroupName: groupNameToSend, targetResourceGroupId } =
              resolveGroupSendOptions(messageAttachments);

            // Send HTML content if it has formatting, otherwise send plain text
            const contentToSend = currentHtmlContent !== '<p><br></p>' && currentHtmlContent.trim() 
              ? currentHtmlContent 
              : currentTextContent.trim();

            // Clear input immediately (optimistic update - message already appears in chat)
            setIsSending(true);
            setShowVaultMenu(false);
            setSelectedVaultIds([]);
            setVaultSearchQuery('');
            quillEditor.setText('');
            setMessage('');
            setAttachmentComposeOpen(false);
            setAttachments([]);
            resetAttachmentGroupState();
            clearComposerLinkPreview();
            finalTranscriptRef.current = '';
            interimTranscriptRef.current = '';
            
            // Clear draft after sending message
            if (channelId) {
              if (draftSaveTimeoutRef.current) {
                clearTimeout(draftSaveTimeoutRef.current);
                draftSaveTimeoutRef.current = null;
              }
              clearDraft(channelId);
              latestDraftContentRef.current = '';
            }
            
            // Clear reply state after sending
            if (onCancelReply) {
              onCancelReply();
            }
            
            // Stop typing indicator when message is sent
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            setIsTyping(false);
            notifyTyping(false);

            const mentionedUserIds = collectMentionedUserIds(currentTextContent);
            mentionedUserIdsRef.current.clear();

            await onSendMessage(
              contentToSend,
              messageAttachments,
              replyToMessage?.id,
              linkPreview,
              mentionedUserIds,
              groupNameToSend,
              targetResourceGroupId,
              {
                sensitive: sensitiveTaggedRef.current,
                senderPin: senderPinForSendRef.current || undefined,
                allowReceiverPin: allowReceiverPinForSendRef.current,
                sensitiveTitle: sensitiveTitleForSendRef.current || undefined,
              }
            );
            setSensitiveTagged(false);
            senderPinForSendRef.current = '';
            allowReceiverPinForSendRef.current = true;
            sensitiveTitleForSendRef.current = '';
          } catch (error) {
            console.error('Error sending message:', error);
            // Don't restore the message on error - it's already shown in chat with failed status
            // User can retry using the retry button
            // The error notification is already shown by handleSendMessage
          } finally {
            setIsSending(false);
          }
          };
          if (sensitiveTaggedRef.current) {
            pendingSensitiveActionRef.current = proceed;
            setSensitiveSendOpen(true);
            return;
          }
          await proceed();
        }
      }
    };

    // Attach to the editor element with capture phase to catch before Quill handles it
    const editorElement = quillEditor.root;
    editorElement.addEventListener('keydown', handleKeyDown, true);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [attachments, attachmentGroupMode, attachmentGroupName, attachmentExistingGroupId, channelId, replyToMessage, onSendMessage, onCancelReply, onTyping, showMentionSuggestions, mentionSuggestions, mentionType, selectedMentionIndex, linkPreview, collectMentionedUserIds, dispatch, resolveGroupSendOptions, resetAttachmentGroupState, isSending]);

  const resetFileInputs = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (audioInputRef.current) audioInputRef.current.value = '';
  }, []);

  const uploadOrLocalizeFiles = useCallback(async (fileEntries) => {
    if (!fileEntries?.length) return [];

    const finishWithLocal = async () => {
      const localFiles = await filesToLocalAttachments(fileEntries);
      if (!localFiles.length) {
        throw new Error('Could not save files locally');
      }
      return localFiles;
    };

    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (!offline) {
      try {
        const token = localStorage.getItem('token');
        const results = await uploadFilesWithProgress({
          apiUrl: API_URL,
          token,
          files: fileEntries,
          onProgress: setUploadProgress,
          timeout: 120000,
        });
        return results.map(({ data, fileType }, index) => {
          const file = { ...data };
          file.fileType = inferAttachmentFileType({ ...file, fileType: fileType || file.fileType });
          if (Array.isArray(fileEntries[index]?.vaultContents) && fileEntries[index].vaultContents.length) {
            file.vaultContents = fileEntries[index].vaultContents;
          }
          return file;
        });
      } catch (error) {
        if (!shouldUseOfflineAttachment(error) && error?.message !== 'Voice note audio is empty') {
          console.warn('Upload failed, keeping file locally:', error);
        } else if (error?.message === 'Voice note audio is empty') {
          throw error;
        }
      }
    }
    return finishWithLocal();
  }, []);

  const attachSelectedFiles = useCallback(async (fileEntries, { pasted = false } = {}) => {
    if (!fileEntries?.length) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const processedFiles = await uploadOrLocalizeFiles(fileEntries);
      if (!processedFiles.length) return;
      setAttachments((prev) => [...prev, ...processedFiles]);
      if (pasted) {
        dispatch(addNotification({
          type: 'success',
          title: processedFiles.length > 1 ? 'Files Pasted' : 'File Pasted',
          message: pastedAttachmentSummary(processedFiles),
        }));
      } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        dispatch(addNotification({
          type: 'info',
          title: 'Attached offline',
          message: 'Files will upload when you are back online.',
        }));
      }
    } catch (error) {
      console.error('Error attaching file:', error);
      if (error.response?.status === 413) {
        dispatch(addNotification({
          type: 'error',
          title: 'File Too Large',
          message: 'File size exceeds 20MB limit.',
        }));
      } else {
        dispatch(addNotification({
          type: 'error',
          title: pasted ? 'Upload Failed' : 'Attach Failed',
          message: pasted
            ? 'Failed to upload pasted file(s). Please try again.'
            : 'Could not attach this file. Please try again.',
        }));
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      resetFileInputs();
    }
  }, [dispatch, resetFileInputs, uploadOrLocalizeFiles]);

  const readComposerPlainText = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    return String(editor?.getText?.() || composerPlainText || '').replace(/\n+$/, '');
  }, [composerPlainText]);

  const openTextExportPrompt = useCallback((kind) => {
    const text = readComposerPlainText();
    if (text.replace(/\s+/g, ' ').trim().length <= LONG_TEXT_CHARS) return;
    const extension = kind === 'pdf' ? 'pdf' : 'txt';
    setExportFileName(stampFileName(extension).replace(/\.[^.]+$/, ''));
    setTextExportPrompt({ kind, text });
  }, [readComposerPlainText]);

  const closeTextExportPrompt = useCallback(() => {
    setTextExportPrompt(null);
    setExportFileName('');
  }, []);

  const wipeComposerText = useCallback(() => {
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
      draftSaveTimeoutRef.current = null;
    }
    latestDraftContentRef.current = '';
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      editor.setText('', 'silent');
    }
    setMessage('');
    if (channelId) {
      clearDraft(channelId);
    }
  }, [channelId]);

  const confirmTextExport = useCallback(() => {
    if (!textExportPrompt || isSending || isUploading) return;
    const { kind, text } = textExportPrompt;
    const name = exportFileName;
    const replyId = replyToMessage?.id;
    wipeComposerText();
    setAttachments([]);
    setAttachmentComposeOpen(false);
    resetAttachmentGroupState();
    clearComposerLinkPreview();
    closeTextExportPrompt();

    const sendFile = async () => {
      try {
        const file = kind === 'pdf'
          ? textToPdfFile(text, name)
          : textToTxtFile(text, name);
        setIsUploading(true);
        setIsSending(true);
        const processed = await uploadOrLocalizeFiles([file]);
        if (!processed.length) {
          throw new Error('Could not attach the converted file.');
        }
        await onSendMessage(
          '',
          mapAttachmentsForSend(processed),
          replyId,
          null,
          [],
          undefined,
          undefined,
          {
            sensitive: sensitiveTaggedRef.current,
            senderPin: senderPinForSendRef.current || undefined,
            allowReceiverPin: allowReceiverPinForSendRef.current,
            sensitiveTitle: sensitiveTitleForSendRef.current || undefined,
          },
        );
        setSensitiveTagged(false);
        senderPinForSendRef.current = '';
        allowReceiverPinForSendRef.current = true;
        sensitiveTitleForSendRef.current = '';
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: error?.response?.data?.error || error?.message || 'Could not convert this text.',
        }));
      } finally {
        setIsUploading(false);
        setIsSending(false);
        wipeComposerText();
      }
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sendFile();
      });
    });
  }, [
    closeTextExportPrompt,
    dispatch,
    exportFileName,
    isSending,
    isUploading,
    onSendMessage,
    replyToMessage?.id,
    resetAttachmentGroupState,
    textExportPrompt,
    uploadOrLocalizeFiles,
    wipeComposerText,
  ]);

  const clearComposerAfterSend = useCallback(() => {
    const quillEditor = quillRef.current?.getEditor();
    if (quillEditor) {
      quillEditor.setText('');
    }
    setMessage('');
    setAttachmentComposeOpen(false);
    setAttachments([]);
    resetAttachmentGroupState();
    clearComposerLinkPreview();
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    if (channelId) {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
        draftSaveTimeoutRef.current = null;
      }
      clearDraft(channelId);
      latestDraftContentRef.current = '';
    }
    if (onCancelReply) {
      onCancelReply();
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (isTyping) {
      setIsTyping(false);
      notifyTyping(false);
    }
    mentionedUserIdsRef.current.clear();
  }, [channelId, isTyping, onCancelReply, resetAttachmentGroupState]);

  const sendVoiceNote = useCallback(async ({ includeAudio, includeCaption = true, caption: captionOverride } = {}) => {
    const review = voiceApiRef.current?.review;
    if (!review || isSending) return;

    const caption = includeCaption
      ? String(
          captionOverride
          ?? voiceApiRef.current?.getCaption?.()
          ?? review.caption
          ?? ''
        ).trim()
      : '';
    if (!includeAudio && !caption) {
      dispatch(addNotification({
        type: 'error',
        title: 'Voice note',
        message: 'Choose audio only, caption only, or audio with caption.',
      }));
      return;
    }
    if (includeCaption && !caption) {
      dispatch(addNotification({
        type: 'error',
        title: 'Voice note',
        message: 'Type a caption in the box, or send audio only.',
      }));
      return;
    }

    try {
      setIsSending(true);
      let voiceAttachments = [];
      if (includeAudio && review.blob) {
        setIsUploading(true);
        setUploadProgress(0);
        try {
          if (!review.blob || review.blob.size < 64) {
            throw new Error('Voice note audio is empty');
          }
          let durationSeconds = Number(review.durationSeconds);
          if (!Number.isFinite(durationSeconds) || durationSeconds < 0.5) {
            durationSeconds = await readBlobDurationSeconds(review.blob);
          }
          const voiceFile = await blobToVoiceFile(review.blob, durationSeconds);
          voiceAttachments = await uploadOrLocalizeFiles([{
            file: voiceFile,
            fileType: 'AUDIO',
          }]);
          if (Number.isFinite(durationSeconds) && durationSeconds >= 0.5) {
            voiceAttachments = voiceAttachments.map((att) => ({
              ...att,
              durationSeconds,
            }));
          }
        } catch (error) {
          console.error('Error uploading voice note:', error);
          const detail = error?.response?.data;
          const detailText = typeof detail === 'string'
            ? detail
            : (detail?.error || detail?.message || error?.message || '');
          dispatch(addNotification({
            type: 'error',
            title: 'Upload Failed',
            message: detailText
              ? `Could not upload the voice note. ${detailText}`
              : 'Could not upload the voice note. Please try again.',
          }));
          return;
        } finally {
          setIsUploading(false);
          setUploadProgress(0);
        }
      }

      const messageAttachments = mapAttachmentsForSend([
        ...attachments,
        ...voiceAttachments,
      ]);
      const { attachmentGroupName: groupNameToSend, targetResourceGroupId } =
        resolveGroupSendOptions(messageAttachments);
      const contentToSend = captionToHtml(caption);
      const mentionedUserIds = collectMentionedUserIds(caption);

      clearComposerAfterSend();
      voiceApiRef.current?.discardReview();

      await onSendMessage(
        contentToSend,
        messageAttachments,
        replyToMessage?.id,
        null,
        mentionedUserIds,
        groupNameToSend,
        targetResourceGroupId,
        {
          sensitive: sensitiveTaggedRef.current,
          senderPin: senderPinForSendRef.current || undefined,
          allowReceiverPin: allowReceiverPinForSendRef.current,
          sensitiveTitle: sensitiveTitleForSendRef.current || undefined,
        }
      );
      setSensitiveTagged(false);
      senderPinForSendRef.current = '';
      allowReceiverPinForSendRef.current = true;
      sensitiveTitleForSendRef.current = '';
    } catch (error) {
      console.error('Error sending voice note:', error);
    } finally {
      setIsSending(false);
    }
  }, [
    attachments,
    clearComposerAfterSend,
    collectMentionedUserIds,
    dispatch,
    isSending,
    onSendMessage,
    replyToMessage?.id,
    resolveGroupSendOptions,
    uploadOrLocalizeFiles,
  ]);

  const insertVaultContent = (html) => {
    if (!html || !stripHtmlTags(html)) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      setMessage((prev) => (stripHtmlTags(prev) ? `${prev}<p><br></p>${html}` : html));
      return;
    }
    const current = editor.root.innerHTML;
    const next = stripHtmlTags(current) ? `${current}<p><br></p>${html}` : html;
    editor.clipboard.dangerouslyPasteHTML(next);
    setMessage(editor.root.innerHTML);
  };

  const addVaultItemsToComposer = async (items) => {
    const selected = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!selected.length || vaultBusy) return;
    setVaultBusy(true);
    try {
      if (selected.length > 1) {
        const zipFile = await buildVaultZipFile(selected);
        if ((zipFile?.size || 0) > MAX_FILE_SIZE) {
          dispatch(addNotification({
            type: 'error',
            title: 'File Too Large',
            message: 'The zip is larger than 20MB. Select fewer documents.',
          }));
          return;
        }
        await attachSelectedFiles([{
          file: zipFile,
          fileType: 'FILE',
          vaultContents: buildVaultZipContents(selected),
        }]);
      } else {
        const item = selected[0];
        const files = (item.attachments || []).filter((att) => att?.fileUrl);
        if (files.length) {
          setAttachments((prev) => [
            ...prev,
            ...files.map((att) => ({
              id: att.id || `vault-${Date.now()}-${att.fileUrl}`,
              fileName: att.fileName || item.title || 'file',
              fileUrl: att.fileUrl,
              fileType: inferAttachmentFileType(att),
              fileSize: att.fileSize || 0,
            })),
          ]);
        }
        insertVaultContent(item.content);
      }
      setShowVaultMenu(false);
      setSelectedVaultIds([]);
      setVaultSearchQuery('');
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Vault',
        message: error?.response?.data?.error || error?.message || 'Could not add vault documents',
      }));
    } finally {
      setVaultBusy(false);
    }
  };

  const applyVaultSelection = async () => {
    const selected = vaultItems.filter((item) => selectedVaultIds.includes(vaultItemId(item)));
    await addVaultItemsToComposer(selected);
  };

  // Handle paste event for images and files (clipboard paste - Ctrl+V / Cmd+V)
  useEffect(() => {
    const quillEditor = quillRef.current?.getEditor();
    if (!quillEditor) return;

    const handlePaste = async (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items || items.length === 0) return;

      // Collect all files from clipboard
      const filesToUpload = [];
      const oversizedFiles = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Check if item is a file (image, video, audio, or other file)
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          if (!blob) continue;

          // Validate file size (20MB limit)
          if (blob.size > MAX_FILE_SIZE) {
            oversizedFiles.push(blob);
            continue;
          }

          // Determine file type and preserve clipboard-provided name when available
          const clipboardName = (blob.name || '').trim();
          const hasClipboardName = clipboardName.length > 0;
          let fileName = '';
          let fileType = 'FILE';

          if (item.type.indexOf('image') !== -1) {
            const extension = blob.type.split('/')[1] || 'png';
            fileName = hasClipboardName ? clipboardName : `pasted-image-${Date.now()}-${i}.${extension}`;
            fileType = 'IMAGE';
          } else if (item.type.indexOf('video') !== -1) {
            const extension = blob.type.split('/')[1] || 'mp4';
            fileName = hasClipboardName ? clipboardName : `pasted-video-${Date.now()}-${i}.${extension}`;
            fileType = 'VIDEO';
          } else if (item.type.indexOf('audio') !== -1) {
            const extension = blob.type.split('/')[1] || 'mp3';
            fileName = hasClipboardName ? clipboardName : `pasted-audio-${Date.now()}-${i}.${extension}`;
            fileType = 'AUDIO';
          } else {
            // Try to detect file type from MIME type or use generic name
            const mimeParts = blob.type.split('/');
            const extension = mimeParts[1] || 'bin';
            fileName = hasClipboardName ? clipboardName : `pasted-file-${Date.now()}-${i}.${extension}`;
            fileType = 'FILE';
          }

          // Create a File object from the blob
          const file = new File([blob], withUnderscoresForUpload(fileName), { type: blob.type });
          filesToUpload.push({ file, fileType });
        }
      }

      // If no files found, allow default paste behavior
      if (filesToUpload.length === 0) {
        // Show error if there were oversized files
        if (oversizedFiles.length > 0) {
            dispatch(addNotification({
            type: 'error',
            title: 'File Too Large',
            message: 'Pasted file size must be less than 20MB.',
          }));
        }
        return;
      }

      // Prevent default paste behavior since we're handling files
      e.preventDefault();
      e.stopPropagation();

      // Show error for oversized files if any
      if (oversizedFiles.length > 0) {
            dispatch(addNotification({
              type: 'error',
          title: 'File Too Large',
          message: `${oversizedFiles.length} file(s) exceed the 20MB limit and were not uploaded.`,
        }));
      }

      await attachSelectedFiles(filesToUpload, { pasted: true });
    };

    // Also handle keyboard shortcut Ctrl+V / Cmd+V
    const handleKeyDown = (e) => {
      // Check for Ctrl+V (Windows/Linux) or Cmd+V (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // The paste event will be triggered automatically, but we can ensure focus
        const editorElement = quillEditor.root;
        if (document.activeElement !== editorElement && !editorElement.contains(document.activeElement)) {
          editorElement.focus();
        }
      }
    };

    const editorElement = quillEditor.root;
    editorElement.addEventListener('paste', handlePaste);
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('paste', handlePaste);
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, attachSelectedFiles]);

  // Cleanup timeouts on unmount (draft flush runs in channel useLayoutEffect cleanup).
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const oversizedFiles = Array.from(files).filter((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      dispatch(addNotification({
        type: 'error',
        title: 'File Too Large',
        message: `File size must be less than 20MB. ${oversizedFiles.map((f) => f.name).join(', ')} exceeds the limit.`,
      }));
      return;
    }

    const fileEntries = Array.from(files).map((file) => ({
      file,
      fileType: inferAttachmentFileType({
        fileName: file.name,
        fileType: (file.type || '').startsWith('image/') ? 'IMAGE' : 'FILE',
      }),
    }));
    await attachSelectedFiles(fileEntries);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => {
      const removed = prev[index];
      if (removed) {
        if (isInlineFileSrc(removed.fileUrl)) {
          try {
            URL.revokeObjectURL(removed.fileUrl);
          } catch {
            // ignore
          }
        }
        if (removed.localFileId || removed.pendingUpload) {
          deleteLocalFile(removed.localFileId || removed.id);
        }
      }
      const next = prev.filter((_, i) => i !== index);
      if (!canGroupComposeAttachments(next)) {
        resetAttachmentGroupState();
      }
      return next;
    });
  };

  const handleReorderAttachments = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setAttachments((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleRenameAttachment = (index, nextBaseName) => {
    const trimmed = (nextBaseName || '').trim();
    if (!trimmed) return;
    setAttachments((prev) => {
      const current = prev[index];
      if (!current) return prev;
      const { extension } = splitFileNameParts(current.fileName);
      const nextName = composeFileName(trimmed, extension) || current.fileName;
      if (nextName === current.fileName) return prev;
      const next = [...prev];
      next[index] = { ...current, fileName: nextName };
      return next;
    });
  };

  const handleBrowseAddImages = () => {
    fileInputRef.current?.click();
  };

  const defaultDriveFolderName = () => {
    const named = String(attachmentGroupName || '').trim();
    if (named) return named;
    return 'WorkLoop files';
  };

  const startSaveAttachedFilesToDrive = async () => {
    const files = attachments.filter((item) => item?.fileUrl && !isLinkAttachment(item));
    if (!files.length || savingToDrive) return;
    try {
      const status = await getGooglePluginStatus();
      if (!status?.driveConnected) {
        setConfirmConnectDrive(true);
        return;
      }
      setDriveFolderName(defaultDriveFolderName());
      setDriveFolderModalOpen(true);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Google Drive',
        message: error?.message || 'Could not check Google Drive.',
      }));
    }
  };

  const saveAttachedFilesToDrive = async (folderName) => {
    const name = String(folderName || driveFolderName || '').trim();
    const files = attachments.filter((item) => item?.fileUrl && !isLinkAttachment(item));
    if (!files.length || !name) return;
    setSavingToDrive(true);
    setDriveUploadDone(0);
    setDriveUploadTotal(files.length);
    try {
      const folder = await uploadAttachmentsToGoogleDrive(files, name, (done, total) => {
        setDriveUploadDone(done);
        setDriveUploadTotal(total);
      });
      const shareUrl = folder?.shareUrl;
      if (!shareUrl) {
        throw new Error('Could not get a Drive folder link.');
      }
      const folderLink = {
        id: folder.id ? `drive-folder-${folder.id}` : `drive-folder-${Date.now()}`,
        fileName: folder.name || name,
        fileUrl: shareUrl,
        fileType: 'LINK',
        fileSize: 0,
        linkUrl: shareUrl,
      };
      setAttachments((prev) => {
        const links = prev.filter((item) => isLinkAttachment(item));
        return [...links, folderLink];
      });
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        // ignore clipboard failures
      }
      setDriveFolderModalOpen(false);
      dispatch(addNotification({
        type: 'success',
        title: 'Saved to Google Drive',
        message: `Folder “${folder.name || name}” link is in the message and copied.`,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Drive upload failed',
        message: error?.message || 'Could not save these files to Google Drive.',
      }));
    } finally {
      setSavingToDrive(false);
    }
  };

  const connectDriveThenSave = async () => {
    setConfirmConnectDrive(false);
    try {
      await connectGooglePlugin('drive');
      const status = await getGooglePluginStatus();
      if (!status?.driveConnected) {
        throw new Error('Google Drive is not connected yet.');
      }
      setDriveFolderName(defaultDriveFolderName());
      setDriveFolderModalOpen(true);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Google Drive',
        message: error?.message || 'Could not connect Google Drive.',
      }));
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 px-4 pb-4 pt-1 bg-white dark:bg-gray-800 flex-shrink-0">
      {/* Link Preview */}
      {linkPreview && (
        <div className="mb-2 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden max-w-md">
          <div className="flex items-start justify-between bg-gray-50 dark:bg-gray-700 p-2">
          <div className="flex-1 min-w-0">
              <a
                href={linkPreview.url ? linkPreview.url.replace(/&amp;/g, '&') : linkPreview.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors rounded p-2"
              >
                {linkPreview.imageUrl && (
                  <div className="w-full h-32 bg-gray-200 dark:bg-gray-600 overflow-hidden rounded mb-2">
                    <img
                      src={linkPreview.imageUrl}
                      alt={linkPreview.title || 'Link preview'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
            </div>
                )}
                <div>
                  {linkPreview.siteName && (
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                      {linkPreview.siteName}
                    </p>
                  )}
                  {linkPreview.title && (
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                      {linkPreview.title}
                    </h4>
                  )}
                  {linkPreview.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                      {linkPreview.description}
                    </p>
                  )}
                  {linkPreview.domain && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                      {linkPreview.domain}
                    </p>
                  )}
                </div>
              </a>
          </div>
          <button
              onClick={() => clearComposerLinkPreview()}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Remove preview"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          </div>
        </div>
      )}

      {/* Reply Preview */}
      {replyToMessage && !isThreadInput && (
        <div
          className="mb-2 flex items-start justify-between bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded-lg p-3 w-full sm:max-w-xl"
          role="region"
          aria-label="Reply preview"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <svg
                className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>

              <span className="text-sm font-medium text-primary-700 dark:text-primary-300 truncate">
                Replying to {replyToMessage.senderName || "Unknown User"}
              </span>
            </div>

            {/* preview text */}
            <p
              className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2"
              title={stripHtmlTags(replyToMessage.content || "Message deleted")}
            >
              {(() => {
                // collapse whitespace so the clamp is consistent
                const raw = replyToMessage.content
                  ? stripHtmlTags(replyToMessage.content)
                  : "Message deleted";
                return raw.replace(/\s+/g, " ").trim();
              })()}
            </p>
            </div>

            <button
            onClick={onCancelReply}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            title="Cancel reply"
            aria-label="Cancel reply"
            type="button"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            </button>
        </div>
      )}

      {/* Upload progress */}
      {isUploading && (
        <div className="mb-2 max-w-md" role="status" aria-live="polite" aria-label={`Uploading files, ${uploadProgress} percent`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Uploading…
            </span>
            <span className="text-xs tabular-nums text-primary-600 dark:text-primary-400 font-semibold">
              {uploadProgress}%
            </span>
            </div>
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-600 transition-[width] duration-150 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
      
      {isLongComposerText ? (
        <div className="mb-2 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={isUploading || isSending}
            onClick={() => openTextExportPrompt('pdf')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-sm hover:from-rose-600 hover:to-orange-600 disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm1 7V3.5L19.5 9H15zM8.2 13.1c.2-.7.8-1.1 1.6-1.1.9 0 1.5.5 1.5 1.3 0 .6-.3 1-.9 1.4l-.8.6h1.8v1.1H8.3v-.9l1.6-1.2c.3-.2.4-.4.4-.6 0-.3-.2-.5-.5-.5-.4 0-.6.3-.7.7l-1.1-.5zm4 3.8v-5h1.1l1.6 3.6h.1l1.6-3.6H18v5h-1.2v-3.3h-.1l-1.4 3.3h-.8l-1.4-3.3h-.1v3.3h-1.2z" />
            </svg>
            Convert to PDF
          </button>
          <button
            type="button"
            disabled={isUploading || isSending}
            onClick={() => openTextExportPrompt('txt')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow-sm hover:from-sky-600 hover:to-indigo-600 disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3v6h6M8 13h8M8 17h5" />
            </svg>
            Convert to TXT
          </button>
        </div>
      ) : null}

      {textExportPrompt ? createPortal(
        <div className="fixed inset-0 z-[2100] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={closeTextExportPrompt} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {textExportPrompt.kind === 'pdf' ? 'Name this PDF' : 'Name this text file'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                The original text will not be sent in the chat. Only this file will go.
              </p>
              <input
                autoFocus
                type="text"
                value={exportFileName}
                onChange={(e) => setExportFileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmTextExport();
                  }
                }}
                placeholder={textExportPrompt.kind === 'pdf' ? 'PDF name' : 'File name'}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeTextExportPrompt}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isUploading || isSending || !String(exportFileName || '').trim()}
                  onClick={confirmTextExport}
                  className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {isUploading || isSending ? 'Sending…' : 'Convert and send'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="mb-2">
          <AttachmentGroupBar
            attachments={attachments}
            groupName={attachmentGroupName}
            groupMode={attachmentGroupMode}
            existingGroupId={attachmentExistingGroupId}
            existingGroups={existingResourceGroups}
            expanded={attachmentGroupExpanded}
            onExpandedChange={setAttachmentGroupExpanded}
            onViewImage={(images, index) => setViewingGallery({ images, index })}
            onViewAll={() => setViewingAllAttachments(true)}
            onRemove={handleRemoveAttachment}
            onArrange={() => setAttachmentComposeOpen(true)}
            onSaveToDrive={startSaveAttachedFilesToDrive}
            savingToDrive={savingToDrive}
            driveUploadDone={driveUploadDone}
            driveUploadTotal={driveUploadTotal}
            mode="compose"
          />
        </div>
      )}

      {attachmentComposeOpen && attachments.length >= 2 && (
        <AttachmentComposeModal
          attachments={attachments}
          groupName={attachmentGroupName}
          onGroupNameChange={setAttachmentGroupName}
          groupMode={attachmentGroupMode}
          onGroupModeChange={setAttachmentGroupMode}
          existingGroupId={attachmentExistingGroupId}
          onExistingGroupIdChange={setAttachmentExistingGroupId}
          existingGroups={existingResourceGroups}
          loadingExistingGroups={loadingResourceGroups}
          onClose={() => setAttachmentComposeOpen(false)}
          onReorder={handleReorderAttachments}
          onRemove={handleRemoveAttachment}
          onBrowseAdd={handleBrowseAddImages}
          onRename={handleRenameAttachment}
          onSaveToDrive={startSaveAttachedFilesToDrive}
          savingToDrive={savingToDrive}
          driveUploadDone={driveUploadDone}
          driveUploadTotal={driveUploadTotal}
          onEditedImage={async (_attachment, file, index) => {
            setAttachments((prev) => prev.map((item, idx) => {
              if (idx !== index) return item;
              return {
                ...item,
                file,
                fileName: file.name,
                fileType: 'IMAGE',
                fileSize: file.size,
                fileUrl: URL.createObjectURL(file),
                previousFileUrl: item.previousFileUrl || item.fileUrl,
                previousFileName: item.previousFileName || item.fileName,
              };
            }));
          }}
        />
      )}

      {viewingAllAttachments && (
        <AttachmentsListModal
          attachments={attachments}
          onClose={() => setViewingAllAttachments(false)}
          onViewImage={(images, index) => {
            setViewingAllAttachments(false);
            setViewingGallery({ images, index });
          }}
          onRemove={(index) => {
            handleRemoveAttachment(index);
            if (attachments.length <= 1) {
              setViewingAllAttachments(false);
                      }
                    }}
                  />
      )}

      {viewingGallery && (
        <ImageViewerModal
          images={viewingGallery.images}
          initialIndex={viewingGallery.index}
          onClose={() => setViewingGallery(null)}
          onEditedImage={async (attachment, file, index) => {
            setAttachments((prev) => prev.map((item, idx) => {
              const sameByIndex = typeof index === 'number' && idx === index;
              const same = sameByIndex
                || (attachment.id && item.id && item.id === attachment.id)
                || (item.localFileId && item.localFileId === attachment.localFileId)
                || item.fileUrl === attachment.fileUrl
                || (attachment.previousFileUrl && item.fileUrl === attachment.previousFileUrl)
                || (item.previousFileUrl
                  && attachment.previousFileUrl
                  && item.previousFileUrl === attachment.previousFileUrl
                  && idx === index);
              if (!same) return item;
              const previewUrl = URL.createObjectURL(file);
              return {
                ...item,
                file,
                fileName: file.name,
                fileType: 'IMAGE',
                fileSize: file.size,
                fileUrl: previewUrl,
                previousFileUrl: item.previousFileUrl || item.fileUrl,
                previousFileName: item.previousFileName || item.fileName,
              };
            }));
          }}
        />
      )}
      <form 
        onSubmit={handleSubmit} 
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={`flex-1 relative flex flex-col ${isDragging ? "ring-2 ring-primary-500 ring-offset-2 rounded-lg" : ""
            }`}
        >
          {isDragging && (
            <div className="absolute inset-0 bg-primary-50 dark:bg-primary-900/20 border-2 border-dashed border-primary-500 rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-primary-500 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  Drop files here to attach
                </p>
              </div>
            </div>
          )}
          
          <VoiceNoteBanner
            recording={voice.recording}
            seconds={voice.seconds}
            caption={voice.liveCaption}
            captionAfterStop={Boolean(window.workloopRealm)}
            speechSupported={voice.speechSupported}
            speechHint={voice.speechHint}
            captionPrompt={voice.captionPrompt}
            onAllowCaptions={() => voice.startSpeechRecognition()}
            onDeclineCaptions={() => voice.declineCaptionPermission()}
            onStop={() => voice.stopRecording()}
            onCancel={() => voice.cancelRecording()}
          />

          <div ref={quickReplyBoxRef} className="relative mb-0">
            <div className="flex items-center gap-1 min-h-[22px]">
              <div className="min-w-0 flex-1 flex items-center justify-center gap-1.5 overflow-x-auto">
                {suggestedReplies.filter((reply) => reply !== CALL_ME_BACK_PLEASE && reply !== CALL_THIS_PERSON && reply !== WILL_CALL_IN_SOMETIME).map((reply) => (
                  <button
                    key={reply}
                    type="button"
                    disabled={isSending}
                    onClick={() => sendSuggestedReply(reply)}
                    className="whitespace-nowrap rounded-full border border-primary-500 bg-white px-2.5 py-0.5 text-[11px] leading-4 font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-400 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-700"
                  >
                    {reply}
                  </button>
                ))}
              </div>
              <button
                type="button"
                aria-label="Quick messages"
                title="Quick messages"
                onClick={() => setShowQuickReplies((open) => !open)}
                className={`flex-shrink-0 rounded-full p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 ${showQuickReplies ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                <svg className={`h-4 w-4 transition-transform ${showQuickReplies ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {showQuickReplies ? (
              <div className="absolute bottom-full right-0 z-50 mb-0.5 flex w-[40%] max-h-56 flex-col rounded-xl border border-primary-200 bg-gradient-to-br from-violet-50 via-sky-50 to-amber-50 p-2 shadow-md dark:border-primary-500/40 dark:from-violet-950/50 dark:via-sky-950/40 dark:to-amber-950/30">
                <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="flex flex-wrap items-center gap-1.5">
                  {isDirectChannel(liveChannel) ? (
                    <>
                  <button
                    type="button"
                    disabled={isSending}
                    onClick={() => sendSuggestedReply(CALL_ME_BACK_PLEASE)}
                    className="whitespace-nowrap rounded-full px-2.5 py-0.5 text-center text-[11px] leading-4 font-semibold text-white shadow-sm bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 hover:from-amber-500 hover:via-orange-500 hover:to-rose-500 disabled:opacity-50"
                    title="Send and add you to their Need to call"
                  >
                    {CALL_ME_BACK_PLEASE}
                  </button>
                  <button
                    type="button"
                    disabled={isSending}
                    onClick={() => {
                      setShowQuickReplies(false);
                      setCallThisPersonOpen(true);
                    }}
                    className="whitespace-nowrap rounded-full px-2.5 py-0.5 text-center text-[11px] leading-4 font-semibold text-white shadow-sm bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-600 hover:to-violet-600 disabled:opacity-50"
                    title="Add someone to their Need to call"
                  >
                    {CALL_THIS_PERSON}
                  </button>
                  <button
                    type="button"
                    disabled={isSending}
                    onClick={() => sendSuggestedReply(WILL_CALL_IN_SOMETIME)}
                    className="whitespace-nowrap rounded-full px-2.5 py-0.5 text-center text-[11px] leading-4 font-semibold text-white shadow-sm bg-gradient-to-r from-emerald-400 via-teal-500 to-sky-500 hover:from-emerald-500 hover:via-teal-600 hover:to-sky-600 disabled:opacity-50"
                    title="Send and add them to your Need to call"
                  >
                    {WILL_CALL_IN_SOMETIME}
                  </button>
                    </>
                  ) : null}
                  {DEFAULT_QUICK_REPLIES.filter((reply) => reply !== CALL_ME_BACK_PLEASE && reply !== CALL_THIS_PERSON && reply !== WILL_CALL_IN_SOMETIME).map((reply) => (
                    <button
                      key={reply}
                      type="button"
                      disabled={isSending}
                      onClick={() => sendSuggestedReply(reply)}
                      className="whitespace-nowrap rounded-full border border-primary-500 bg-white px-2.5 py-0.5 text-center text-[11px] leading-4 font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-400 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-700"
                    >
                      {reply}
                    </button>
                  ))}
                  {customQuickReplies.filter((item) => item.length <= 24 && !DEFAULT_QUICK_REPLIES.includes(item)).map((reply) => (
                    <span key={reply} className="relative flex items-center">
                      <button
                        type="button"
                        disabled={isSending}
                        onClick={() => sendSuggestedReply(reply)}
                        className="whitespace-nowrap rounded-full border border-primary-500 bg-white px-2.5 py-0.5 pr-5 text-center text-[11px] leading-4 font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-400 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-700"
                      >
                        {reply}
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${reply}`}
                        className="absolute right-0.5 rounded-full p-0.5 text-gray-400 hover:text-red-500"
                        onClick={() => {
                          const next = customQuickReplies.filter((item) => item !== reply);
                          setCustomQuickReplies(next);
                          saveCustomQuickReplies(currentUser?.id, next);
                        }}
                      >
                        <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
                {customQuickReplies.some((item) => item.length > 24) ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {customQuickReplies.filter((item) => item.length > 24).map((reply) => (
                      <span key={reply} className="relative flex items-center">
                        <button
                          type="button"
                          disabled={isSending}
                          onClick={() => sendSuggestedReply(reply)}
                          className="max-w-full break-words rounded-full border border-primary-500 bg-white px-2.5 py-0.5 pr-5 text-center text-[11px] leading-4 font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-400 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-gray-700"
                          title={reply}
                        >
                          {reply}
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${reply}`}
                          className="absolute right-0.5 rounded-full p-0.5 text-gray-400 hover:text-red-500"
                          onClick={() => {
                            const next = customQuickReplies.filter((item) => item !== reply);
                            setCustomQuickReplies(next);
                            saveCustomQuickReplies(currentUser?.id, next);
                          }}
                        >
                          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                </div>
                <div className="mt-1.5 flex shrink-0 items-center gap-1.5 border-t border-gray-100 pt-1.5 dark:border-gray-700">
                  <input
                    value={quickReplyDraft}
                    onChange={(event) => setQuickReplyDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      event.stopPropagation();
                      const next = String(quickReplyDraft || '').trim();
                      if (!next) return;
                      if (DEFAULT_QUICK_REPLIES.includes(next) || customQuickReplies.includes(next)) {
                        setQuickReplyDraft('');
                        return;
                      }
                      const updated = [...customQuickReplies, next];
                      setCustomQuickReplies(updated);
                      saveCustomQuickReplies(currentUser?.id, updated);
                      setQuickReplyDraft('');
                    }}
                    maxLength={80}
                    placeholder="Add…"
                    className="min-w-0 flex-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-800 outline-none focus:border-primary-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = String(quickReplyDraft || '').trim();
                      if (!next) return;
                      if (DEFAULT_QUICK_REPLIES.includes(next) || customQuickReplies.includes(next)) {
                        setQuickReplyDraft('');
                        return;
                      }
                      const updated = [...customQuickReplies, next];
                      setCustomQuickReplies(updated);
                      saveCustomQuickReplies(currentUser?.id, updated);
                      setQuickReplyDraft('');
                    }}
                    className="flex-shrink-0 rounded-full bg-primary-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-primary-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* Rich Text Editor */}
          <div
            id={composerInstanceId}
            className="border-l border-r border-t border-gray-300 dark:border-gray-600 rounded-t-lg bg-white dark:bg-gray-700 relative overflow-visible"
            style={{ position: 'relative' }}
          >
            <ComposerFormatToolbar
              quillRef={quillRef}
              activeFormats={activeFormats}
              onToggleEmoji={toggleEmojiPicker}
              emojiActive={showEmojiPicker}
              sensitiveTagged={sensitiveTagged}
              onToggleSensitive={() => setSensitiveTagged((value) => !value)}
            />
            <ReactQuill
              key={composerInstanceId}
              ref={quillRef}
              theme="snow"
              value={message}
              onChange={handleQuillChange}
              modules={quillModules}
              formats={QUILL_FORMATS}
              placeholder={isThreadInput ? "Reply to thread..." : "Type a message..."}
              className={`bg-transparent chat-composer-quill chat-composer-quill--${isThreadInput ? 'thread' : 'main'}`}
              style={{
                minHeight: "20px",
              }}
            />

            {/* Mention Suggestions Dropdown */}
            {showMentionSuggestions && mentionSuggestions.length > 0 && (
              <div
                ref={mentionDropdownRef}
                className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                data-mention-suggestions
              >
                {mentionType === 'channel' ? (
                  mentionSuggestions.map((channel, index) => (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => insertChannelMention(channel)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${index === selectedMentionIndex
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : ''
                        }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        #
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {channel.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          #{channelMentionToken(channel)}
                          {channel.isPrivate ? ' · Private' : ''}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  mentionSuggestions.map((user, index) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => insertUserMention(user)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${index === selectedMentionIndex
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : ''
                        }`}
                    >
                      {user.isEveryoneMention ? (
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          @
                        </div>
                      ) : (
                      <PresignedAvatar
                        fileKey={user.profilePicture}
                        name={`${user.firstName || ''} ${user.lastName || ''}`}
                        initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        fallbackClassName="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                      />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.isEveryoneMention ? 'all' : `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.isEveryoneMention
                            ? `Notify everyone in this channel (${Math.max(channelMemberIds.length - 1, 0)})`
                            : `@${user.username}`}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
            
            {/* Custom Quill Styles */}
            <style>{`
              .chat-composer-quill .ql-toolbar {
                display: none !important;
              }
              .chat-composer-quill .ql-container {
                font-size: 14px;
                min-height: 20px;
                max-height: 180px;
                height: auto !important;
                overflow-y: auto !important;
                overflow-x: hidden;
                border: none !important;
                padding-right: 0 !important;
                margin-right: 0 !important;
              }
              .chat-composer-quill .ql-container::-webkit-scrollbar {
                width: 8px;
              }
              .chat-composer-quill .ql-container::-webkit-scrollbar-track {
                background: transparent;
              }
              .chat-composer-quill .ql-container::-webkit-scrollbar-thumb {
                background: #cbd5e1;
                border-radius: 4px;
              }
              .chat-composer-quill .ql-container::-webkit-scrollbar-thumb:hover {
                background: #94a3b8;
              }
              .dark .chat-composer-quill .ql-container::-webkit-scrollbar-thumb {
                background: #4b5563;
              }
              .dark .chat-composer-quill .ql-container::-webkit-scrollbar-thumb:hover {
                background: #6b7280;
              }
              .chat-composer-quill .ql-editor {
                min-height: 20px;
                color: #111827;
                overflow-x: hidden;
                word-wrap: break-word;
                word-break: break-word;
                padding: 12px !important;
                padding-right: 20px !important;
                font-family: Figtree, Inter, system-ui, sans-serif;
              }
              .chat-composer-quill .ql-editor:focus {
                outline: none;
              }
              .dark .chat-composer-quill .ql-editor {
                color: #f9fafb;
              }
            `}</style>
          </div>

          {/* Action Buttons Container */}


          <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-l border-r border-b border-gray-300 dark:border-gray-600 rounded-b-lg bg-white dark:bg-gray-700">
            {/* Tip Text - Left side */}
            <div className="flex-shrink min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                Shift + Enter to add a new line
              </p>
            </div>

            {/* Buttons - Right side */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isThreadInput ? (
                <div className="relative" ref={vaultMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowVaultMenu((open) => {
                        const next = !open;
                        if (next) {
                          setShowTemplateMenu(false);
                          loadVaultItems();
                        }
                        return next;
                      });
                    }}
                    className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${showVaultMenu ? 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200' : ''}`}
                    title="Vault"
                    aria-label="Vault"
                    aria-expanded={showVaultMenu}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </button>
                  {showVaultMenu ? (
                    <div
                      className="absolute bottom-full right-0 mb-2 w-[22rem] max-h-[24rem] overflow-hidden rounded-xl border border-gray-200/90 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl z-50 flex flex-col"
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <div className="px-3.5 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700/80">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">Vault</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          Click a document to add it to your message. Check several to attach as a zip.
                        </div>
                        <input
                          value={vaultSearchQuery}
                          onChange={(event) => setVaultSearchQuery(event.target.value)}
                          placeholder="Search vault…"
                          className="mt-2 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-xs text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="max-h-52 overflow-y-auto py-1.5 flex-1">
                        {vaultItems.filter((item) => {
                          const query = vaultSearchQuery.trim().toLowerCase();
                          if (!query) return true;
                          return String(item.title || '').toLowerCase().includes(query);
                        }).length === 0 ? (
                          <p className="px-3.5 py-6 text-xs text-center text-gray-400">
                            {vaultItems.length === 0
                              ? 'Save a chat or file from the message menu.'
                              : 'No documents match your search.'}
                          </p>
                        ) : vaultItems.filter((item) => {
                          const query = vaultSearchQuery.trim().toLowerCase();
                          if (!query) return true;
                          return String(item.title || '').toLowerCase().includes(query);
                        }).map((item) => {
                          const id = vaultItemId(item);
                          const checked = selectedVaultIds.includes(id);
                          const fileCount = (item.attachments || []).length;
                          return (
                            <div
                              key={id}
                              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setSelectedVaultIds((prev) => (
                                    checked ? prev.filter((value) => value !== id) : [...prev, id]
                                  ));
                                }}
                                className="rounded border-gray-300 dark:border-gray-600"
                              />
                              <button
                                type="button"
                                className="min-w-0 flex-1 text-left"
                                disabled={vaultBusy}
                                onClick={() => {
                                  if (selectedVaultIds.length > 0) {
                                    setSelectedVaultIds((prev) => (
                                      checked ? prev.filter((value) => value !== id) : [...prev, id]
                                    ));
                                    return;
                                  }
                                  addVaultItemsToComposer([item]);
                                }}
                              >
                                <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{item.title}</p>
                                <p className="text-[10px] text-gray-400">
                                  {fileCount ? `${fileCount} file${fileCount === 1 ? '' : 's'}` : 'Chat'}
                                </p>
                              </button>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-rose-500 px-1"
                                aria-label={`Remove ${item.title}`}
                                onClick={async (event) => {
                                  event.stopPropagation();
                                  try {
                                    await deleteVaultItem(id);
                                    setSelectedVaultIds((prev) => prev.filter((value) => value !== id));
                                    loadVaultItems();
                                  } catch (error) {
                                    dispatch(addNotification({
                                      type: 'error',
                                      message: error?.response?.data?.error || 'Could not delete',
                                    }));
                                  }
                                }}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                        <button
                          type="button"
                          disabled={vaultBusy || selectedVaultIds.length === 0}
                          onClick={applyVaultSelection}
                          className="px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-primary-600 text-white disabled:opacity-50"
                        >
                          {vaultBusy
                            ? 'Adding…'
                            : selectedVaultIds.length > 1
                              ? `Attach zip (${selectedVaultIds.length})`
                              : 'Add to message'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {!isThreadInput && typeof onCreateEvent === 'function' ? (
                <button
                  type="button"
                  onClick={() => onCreateEvent()}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="Create event"
                  aria-label="Create event"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              ) : null}
              {!isThreadInput && channelTemplates.length > 0 && (
                <div className="relative" ref={templateMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTemplateMenu((v) => {
                        const next = !v;
                        if (!next) {
                          setTemplateTypeFilter('ALL');
                          setTemplateSearchQuery('');
                          setTemplateHighlightIndex(0);
                        } else {
                          setTemplateHighlightIndex(0);
                          setTimeout(() => templateSearchInputRef.current?.focus(), 0);
                        }
                        return next;
                      });
                    }}
                    className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${showTemplateMenu ? 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200' : ''}`}
                    title="Message templates"
                    aria-label="Message templates"
                    aria-expanded={showTemplateMenu}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
                    </svg>
                  </button>
                  {showTemplateMenu && (() => {
                    const search = templateSearchQuery.trim().toLowerCase();
                    const filteredTemplates = channelTemplates.filter((tpl) => {
                      const typeOk = templateTypeFilter === 'ALL'
                        || String(tpl.templateType || '').toUpperCase() === templateTypeFilter;
                      if (!typeOk) return false;
                      if (!search) return true;
                      const name = String(tpl.name || '').toLowerCase();
                      const description = String(tpl.description || '').toLowerCase();
                      return name.includes(search) || description.includes(search);
                    });
                    const allActive = templateTypeFilter === 'ALL';
                    const safeHighlightIndex = filteredTemplates.length === 0
                      ? 0
                      : Math.min(templateHighlightIndex, filteredTemplates.length - 1);

                    const closeTemplateMenu = () => {
                      setShowTemplateMenu(false);
                      setTemplateTypeFilter('ALL');
                      setTemplateSearchQuery('');
                      setTemplateHighlightIndex(0);
                    };

                    const selectTemplate = (tpl) => {
                      setSelectedTemplate(tpl);
                      closeTemplateMenu();
                    };

                    const moveHighlight = (direction) => {
                      if (filteredTemplates.length === 0) return;
                      setTemplateHighlightIndex((prev) => {
                        const current = Math.min(prev, filteredTemplates.length - 1);
                        const next = direction > 0
                          ? (current + 1) % filteredTemplates.length
                          : (current - 1 + filteredTemplates.length) % filteredTemplates.length;
                        requestAnimationFrame(() => {
                          const listEl = templateListRef.current;
                          const itemEl = listEl?.querySelector(`[data-template-index="${next}"]`);
                          itemEl?.scrollIntoView({ block: 'nearest' });
                        });
                        return next;
                      });
                    };

                    return (
                    <div className="absolute bottom-full right-0 mb-2 w-[22rem] max-h-[24rem] overflow-hidden rounded-xl border border-gray-200/90 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl shadow-gray-900/10 dark:shadow-black/40 z-50 ring-1 ring-black/5 dark:ring-white/10 flex flex-col">
                      <div className="px-3.5 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700/80 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">Templates</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                              {filteredTemplates.length}
                              {(templateTypeFilter !== 'ALL' || search) ? ` of ${channelTemplates.length}` : ''} available
                            </div>
                          </div>
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-sky-50 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
                            </svg>
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter templates by type">
                          <button
                            type="button"
                            role="tab"
                            aria-selected={allActive}
                            onClick={() => {
                              setTemplateTypeFilter('ALL');
                              setTemplateHighlightIndex(0);
                            }}
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                              allActive
                                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                            }`}
                          >
                            All
                          </button>
                          {TEMPLATE_TYPE_FILTER_ORDER.map((type) => {
                            const typeMeta = resolveTemplateTypeMeta(type);
                            const isActive = templateTypeFilter === type;
                            return (
                              <button
                                key={type}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => {
                                  setTemplateTypeFilter(isActive ? 'ALL' : type);
                                  setTemplateHighlightIndex(0);
                                }}
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                  isActive ? typeMeta.activeChip : typeMeta.chip
                                }`}
                              >
                                <TemplateTypeIcon templateType={type} className="w-3 h-3" />
                                {typeMeta.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div ref={templateListRef} className="max-h-52 overflow-y-auto py-1.5 flex-1">
                        {filteredTemplates.length === 0 ? (
                          <div className="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                            {search ? 'No templates match your search' : 'No templates in this type'}
                          </div>
                        ) : (
                          filteredTemplates.map((tpl, index) => {
                            const typeMeta = resolveTemplateTypeMeta(tpl.templateType);
                            const isHighlighted = index === safeHighlightIndex;
                            return (
                              <button
                                key={tpl.id}
                                type="button"
                                data-template-index={index}
                                onMouseEnter={() => setTemplateHighlightIndex(index)}
                                onClick={() => selectTemplate(tpl)}
                                className="group w-full text-left px-2.5 py-1"
                              >
                                <div className={`flex items-start gap-2.5 rounded-lg px-2 py-2 transition-colors ${
                                  isHighlighted
                                    ? 'bg-sky-50 dark:bg-sky-900/25'
                                    : 'group-hover:bg-sky-50/80 dark:group-hover:bg-sky-900/20'
                                }`}>
                                  <span className={`mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${typeMeta.iconBg}`}>
                                    <TemplateTypeIcon templateType={tpl.templateType} className="w-4 h-4" />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {tpl.name}
                                    </div>
                                    {tpl.description ? (
                                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                        {tpl.description}
                                      </p>
                                    ) : (
                                      <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                        Quick insert into this chat
                                      </p>
                                    )}
                                  </div>
                                  <svg
                                    className={`mt-1.5 w-4 h-4 flex-shrink-0 transition-opacity ${
                                      isHighlighted ? 'text-sky-500 opacity-100' : 'text-gray-300 opacity-0 group-hover:opacity-100 dark:text-gray-500'
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 dark:border-gray-700/80 bg-gray-50/80 dark:bg-gray-800/90">
                        <div className="relative flex-1 min-w-0">
                          <svg
                            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
                          </svg>
                          <input
                            ref={templateSearchInputRef}
                            type="text"
                            value={templateSearchQuery}
                            onChange={(e) => {
                              setTemplateSearchQuery(e.target.value);
                              setTemplateHighlightIndex(0);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                moveHighlight(1);
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                moveHighlight(-1);
                              } else if (e.key === 'Enter') {
                                e.preventDefault();
                                const tpl = filteredTemplates[safeHighlightIndex];
                                if (tpl) selectTemplate(tpl);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                closeTemplateMenu();
                              }
                            }}
                            placeholder="Search templates…"
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 pl-8 pr-2.5 py-1.5 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400"
                            aria-label="Search templates"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => moveHighlight(1)}
                          disabled={filteredTemplates.length === 0}
                          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          title="Next template"
                          aria-label="Next template in list"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              )}
              <button
                ref={bottomEmojiButtonRef}
                type="button"
                onClick={(event) => toggleEmojiPicker(event.currentTarget)}
                className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${showEmojiPicker && emojiPickerAnchorRef.current === bottomEmojiButtonRef.current ? 'bg-gray-100 dark:bg-gray-600' : ''}`}
                title="Insert emoji"
                aria-label="Insert emoji"
                aria-expanded={showEmojiPicker}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>

              <VoiceNoteMicButton
                recording={voice.recording}
                disabled={isUploading || isSending}
                onClick={() => {
                  if (voice.recording) {
                    voice.stopRecording();
                    return;
                  }
                  voice.startRecording();
                }}
              />

              {/* File Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                id={uploadInputId}
                className="hidden"
                accept="*/*"
                multiple
                onChange={handleFileInputChange}
                disabled={isUploading}
              />
              <label
                htmlFor={uploadInputId}
                className={`p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer transition-colors ${isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={isUploading ? `Uploading… ${uploadProgress}%` : 'Attach file (max 20MB)'}
              >
                {isUploading ? (
                  <svg
                    className="w-5 h-5 animate-spin text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                )}
              </label>

              {/* Send Button */}
              <button
                type="submit"
                disabled={
                  (() => {
                    const quillEditor = quillRef.current?.getEditor();
                    if (!quillEditor) return true;

                    const textContent = quillEditor.getText() || '';
                    const htmlContent = quillEditor.root.innerHTML || '';
                    const hasContent = hasMeaningfulContent(htmlContent, textContent);

                    return (!hasContent && attachments.length === 0 && !voice.recording) || isUploading;
                  })()
                }
                className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Send message"
              >
                <svg
                  className="w-7 h-5 rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </form>

      {showEmojiPicker && createPortal(
        <div
          ref={emojiPickerRef}
          className="shadow-lg rounded-lg overflow-hidden"
          style={{
            position: 'fixed',
            top: emojiPickerPosition.top,
            left: emojiPickerPosition.left,
            zIndex: 9999,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <EmojiPicker
            onEmojiClick={(emojiData) => insertEmoji(emojiData.emoji)}
            theme={darkMode ? Theme.DARK : Theme.LIGHT}
            width={EMOJI_PICKER_WIDTH}
            height={EMOJI_PICKER_HEIGHT}
            autoFocusSearch={false}
          />
        </div>,
        document.body,
      )}

      <VoiceNoteSendModal
        isOpen={Boolean(voice.review)}
        caption={voice.review?.caption || ''}
        captioning={voice.captioning}
        onCaptionChange={voice.updateReviewCaption}
        audioUrl={voice.review?.url || ''}
        durationSeconds={voice.review?.durationSeconds}
        sending={isSending || isUploading}
        speechSupported={voice.speechSupported}
        speechHint={voice.speechHint}
        onSendWithAudio={(caption) => sendVoiceNote({ includeAudio: true, includeCaption: true, caption })}
        onSendAudioOnly={() => sendVoiceNote({ includeAudio: true, includeCaption: false })}
        onSendCaptionOnly={(caption) => sendVoiceNote({ includeAudio: false, includeCaption: true, caption })}
        onDiscard={() => voice.discardReview()}
      />

      <FillMessageTemplateModal
        isOpen={Boolean(selectedTemplate)}
        template={selectedTemplate}
        channelId={channelId}
        onClose={() => setSelectedTemplate(null)}
        onPostingChange={onTemplatePostingChange}
      />
      <CallThisPersonModal
        isOpen={callThisPersonOpen && isDirectChannel(liveChannel)}
        onClose={() => setCallThisPersonOpen(false)}
        initialUser={null}
        ownerUserId={callThisPersonInitialUser?.id || null}
        onSaved={({ name }) => {
          dispatch(addNotification({
            type: 'success',
            message: `${name} added to their Need to call`,
          }));
        }}
      />
      <DriveFolderNameModal
        isOpen={driveFolderModalOpen}
        fileCount={attachments.filter((item) => item?.fileUrl && !isLinkAttachment(item)).length}
        value={driveFolderName}
        onChange={setDriveFolderName}
        saving={savingToDrive}
        uploaded={driveUploadDone}
        total={driveUploadTotal}
        onClose={() => {
          if (savingToDrive) return;
          setDriveFolderModalOpen(false);
        }}
        onConfirm={saveAttachedFilesToDrive}
      />
      <ConfirmModal
        isOpen={confirmConnectDrive}
        onClose={() => {
          setConfirmConnectDrive(false);
          setSavingToDrive(false);
        }}
        onConfirm={connectDriveThenSave}
        title="Connect Google Drive"
        message="Connect Google Drive to save these files and share view links in this chat."
        confirmText="Connect"
        type="info"
      />
      <SensitiveSendModal
        open={sensitiveSendOpen}
        onClose={() => {
          pendingSensitiveActionRef.current = null;
          setSensitiveSendOpen(false);
        }}
        onConfirm={async ({ senderPin, allowReceiverPin, sensitiveTitle } = {}) => {
          senderPinForSendRef.current = senderPin || '';
          allowReceiverPinForSendRef.current = allowReceiverPin === true;
          sensitiveTitleForSendRef.current = String(sensitiveTitle || '').trim();
          const action = pendingSensitiveActionRef.current;
          pendingSensitiveActionRef.current = null;
          setSensitiveSendOpen(false);
          if (action) await action();
        }}
      />
    </div>
  );
};

export default MessageInput;
